import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency, Language } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Sparkles, ArrowRight, Cpu, Database, ShieldCheck, Award, Network, 
  Calculator, MessageSquare, X, Briefcase, Layers, Landmark, Scale,
  Shield, UserCheck, CheckCircle2, Crown, ChevronDown, Building2, Info
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { BUSINESS_AGENT_IDS } from './BusinessCenterHubView';
import { TAX_AGENT_IDS } from './TaxCenterHubView';
import { ENERGY_AGENT_IDS } from './EnergyCenterHubView';
import { AGRI_AGENT_IDS } from './AgriCenterHubView';
import { LOGISTICS_AGENT_IDS } from './LogisticsCenterHubView';
import { CYBER_AGENT_IDS } from './CyberCenterHubView';
import { CONSTRUCTION_AGENT_IDS } from './ConstructionCenterHubView';
import { SCIENCE_AGENT_IDS } from './ScienceCenterHubView';
import { OFFICE_AGENT_IDS } from './OfficeCenterHubView';
import { SPACE_AGENT_IDS } from './SpaceCenterHubView';
import { ADMIN_AGENT_IDS } from './AdminCenterHubView';
import { PHD_AGENT_IDS } from './PhdCenterHubView';
import { PHARMA_AGENT_IDS } from './PharmaCenterHubView';
import { POLAND_AGENT_IDS } from './PolandCenterHubView';
import { EU_AGENT_IDS } from './EuCenterHubView';
import { US_AGENT_IDS } from './UsCenterHubView';
import { FRANCE_AGENT_IDS } from './FranceCenterHubView';
import { GERMANY_AGENT_IDS } from './GermanyCenterHubView';
import { ECO_AGENT_IDS } from './EcoCenterHubView';
import SmartAgentDispatcher from './SmartAgentDispatcher';
import SituationalCenterShowcase from './SituationalCenterShowcase';
import { DeepBrainOsShowcaseModal } from './DeepBrainOsShowcaseModal';

interface HubViewProps {
  onLaunchAgent: (target: string, preferredLang?: any, hubContext?: string, dynamicAgentData?: any) => void;
  language: Language;
  currentUserEmail?: string;
  hasEnterprise?: boolean;
  enterpriseName?: string;
  isEnterpriseAdmin?: boolean;
  allowedEnterpriseAgents?: string[];
  pinnedEnterpriseAgents?: string[];
  onTogglePinAgent?: (agentId: string) => void;
  onOpenEnterpriseDashboard?: () => void;
  onOpenCorporateOffice?: () => void;
  onOpenCorporateOfficeInfo?: () => void;
  onOpenPricing?: () => void;
  currency?: Currency;
}

interface AgentItem {
  id: string;
  nameUk: string;
  nameEn: string;
  namePl?: string;
  categoryUk: string;
  categoryEn: string;
  categoryPl?: string;
  descriptionUk: string;
  descriptionEn: string;
  descriptionPl?: string;
  featuresUk: string[];
  featuresEn: string[];
  featuresPl?: string[];
  icon: string;
  actionType: string;
  target: string;
  color: string;
  adminOnly?: boolean;
  model?: string;
}

// Dynamic Icon Loader Helper
const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Cpu className={className} />;
  return <IconComponent className={className} />;
};

// Color theme helper
const getColorClasses = (color: string) => {
  switch (color) {
    case 'red':
      return {
        badgeBg: 'bg-red-50 text-red-500 border-red-200',
        iconBg: 'bg-red-50 border-red-200 text-red-500',
        bulletBg: 'bg-red-500',
        glowBg: 'bg-red-500/[0.02] group-hover:bg-red-500/[0.04]',
        hoverBorder: 'hover:border-red-500/30 font-bold'
      };
    case 'blue':
      return {
        badgeBg: 'bg-blue-50 text-blue-600 border-blue-200',
        iconBg: 'bg-blue-50 border-blue-200 text-blue-600',
        bulletBg: 'bg-blue-600',
        glowBg: 'bg-blue-500/[0.015] group-hover:bg-blue-500/[0.03]',
        hoverBorder: 'hover:border-blue-500/30'
      };
    case 'green':
      return {
        badgeBg: 'bg-green-50 text-green-600 border-green-200',
        iconBg: 'bg-green-50 border-green-200 text-green-600',
        bulletBg: 'bg-green-600',
        glowBg: 'bg-green-500/[0.015] group-hover:bg-green-500/[0.03]',
        hoverBorder: 'hover:border-green-500/30'
      };
    case 'purple':
      return {
        badgeBg: 'bg-purple-50 text-purple-600 border-purple-200',
        iconBg: 'bg-purple-50 border-purple-200 text-purple-600',
        bulletBg: 'bg-purple-600',
        glowBg: 'bg-purple-500/[0.015] group-hover:bg-purple-500/[0.03]',
        hoverBorder: 'hover:border-purple-500/30'
      };
    case 'emerald':
      return {
        badgeBg: 'bg-emerald-50 text-emerald-600 border-emerald-200',
        iconBg: 'bg-emerald-50 border-emerald-200 text-emerald-600',
        bulletBg: 'bg-emerald-600',
        glowBg: 'bg-emerald-500/[0.015] group-hover:bg-emerald-500/[0.03]',
        hoverBorder: 'hover:border-emerald-500/30'
      };
    case 'amber':
      return {
        badgeBg: 'bg-amber-50 text-amber-600 border-amber-200',
        iconBg: 'bg-amber-50 border-amber-200 text-amber-600',
        bulletBg: 'bg-amber-600',
        glowBg: 'bg-amber-500/[0.015] group-hover:bg-amber-500/[0.03]',
        hoverBorder: 'hover:border-amber-500/30'
      };
    default:
      return {
        badgeBg: 'bg-slate-50 text-slate-600 border-slate-200',
        iconBg: 'bg-slate-50 border-slate-200 text-slate-600',
        bulletBg: 'bg-slate-650',
        glowBg: 'bg-slate-500/[0.01] group-hover:bg-slate-500/[0.02]',
        hoverBorder: 'hover:border-slate-500/30'
      };
  }
};

export default function HubView({ 
  onLaunchAgent, 
  language, 
  currentUserEmail, 
  hasEnterprise = false, 
  enterpriseName = '',
  isEnterpriseAdmin = false,
  allowedEnterpriseAgents = [],
  pinnedEnterpriseAgents = [],
  onTogglePinAgent,
  onOpenEnterpriseDashboard,
  onOpenCorporateOffice,
  onOpenCorporateOfficeInfo,
  onOpenPricing,
  currency = 'UAH' 
}: HubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const isDe = language === 'de';
  const isFr = language === 'fr';
  const [isCentersExpanded, setIsCentersExpanded] = useState(false);
  const [isCatalogExpanded, setIsCatalogExpanded] = useState(false);
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number }>>({});
  const [mySubscriptions, setMySubscriptions] = useState<any[]>([]);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackEmail, setFeedbackEmail] = useState(currentUserEmail || '');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [showDeepBrainOsModal, setShowDeepBrainOsModal] = useState(false);
  const [deepBrainOsInitialTab, setDeepBrainOsInitialTab] = useState<'overview' | 'architecture' | 'skills' | 'pricing'>('pricing');

  useEffect(() => {
    if (currentUserEmail) {
      setFeedbackEmail(currentUserEmail);
    }
  }, [currentUserEmail]);

  const handleSendFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackEmail.trim() || !feedbackMessage.trim()) return;

    setIsSendingFeedback(true);
    setFeedbackError(null);
    setFeedbackSuccess(false);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: feedbackEmail,
          message: feedbackMessage
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Не вдалося надіслати повідомлення.' : isPl ? 'Nie udało się wysłać wiadomości.' : isDe ? 'Nachricht konnte nicht gesendet werden.' : isFr ? 'Échec de l\'envoi du message.' : 'Failed to send feedback.'));
      }

      setFeedbackSuccess(true);
      setFeedbackMessage('');
    } catch (err: any) {
      console.error(err);
      setFeedbackError(err.message || (isUk ? 'Сталася помилка.' : isPl ? 'Wystąpił błąd.' : isDe ? 'Ein Fehler ist aufgetreten.' : isFr ? 'Une erreur est survenue.' : 'An error occurred.'));
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const [userProfile, setUserProfile] = useState<any>(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch pricing settings:', err);
      }
    };
    const fetchMySubscriptions = async () => {
      try {
        const res = await fetch('/api/subscription/my-subscriptions');
        if (res.ok) {
          const data = await res.json();
          setMySubscriptions(data);
        }
      } catch (err) {
        console.error('Failed to fetch my subscriptions:', err);
      }
    };
    const fetchUserProfile = async () => {
      try {
        const res = await fetch('/api/user');
        if (res.ok) {
          const data = await res.json();
          if (data.user) setUserProfile(data.user);
        }
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
      }
    };
    fetchPricing();
    fetchMySubscriptions();
    fetchUserProfile();
  }, []);

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(isUk ? 'uk-UA' : isPl ? 'pl-PL' : isDe ? 'de-DE' : isFr ? 'fr-FR' : 'en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const isAdmin = ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(currentUserEmail?.toLowerCase() || '');

  const ALL_GROUP_AGENT_IDS = [
    ...BUSINESS_AGENT_IDS,
    ...TAX_AGENT_IDS,
    ...ENERGY_AGENT_IDS,
    ...AGRI_AGENT_IDS,
    ...LOGISTICS_AGENT_IDS,
    ...CYBER_AGENT_IDS,
    ...CONSTRUCTION_AGENT_IDS,
    ...SCIENCE_AGENT_IDS,
    ...OFFICE_AGENT_IDS,
    ...SPACE_AGENT_IDS,
    ...ADMIN_AGENT_IDS,
    ...PHD_AGENT_IDS,
    ...PHARMA_AGENT_IDS,
    ...POLAND_AGENT_IDS,
    ...EU_AGENT_IDS,
    ...US_AGENT_IDS,
    ...FRANCE_AGENT_IDS,
    ...GERMANY_AGENT_IDS,
    ...ECO_AGENT_IDS,
  ];

  const [allAgentsConfig, setAllAgentsConfig] = useState<any[]>(agentsConfig as any[]);

  useEffect(() => {
    fetch('/api/agents-config')
      .then(res => res.json())
      .then(data => {
        if (data.ok && Array.isArray(data.agents)) {
          setAllAgentsConfig(data.agents);
        }
      })
      .catch(() => {});
  }, []);

  const enterpriseAgent = allAgentsConfig.find((a: any) => a.id === 'enterprise-dashboard') as AgentItem | undefined;

  const standaloneAgents = allAgentsConfig.filter((a: any) => {
    if (a.adminOnly && !isAdmin) return false;
    if (a.id === 'enterprise-dashboard') return false;
    // Explicitly retain deep-research on the main page standalone grid
    if (a.id === 'deep-research') return true;
    // Exclude any agent that already belongs to one of the 10 group hubs
    return !ALL_GROUP_AGENT_IDS.includes(a.id);
  }) as AgentItem[];

  const handleLaunchAgent = (agent: AgentItem) => {
    if (agent.actionType === 'tab') {
      onLaunchAgent(agent.target);
    } else {
      window.open(agent.target, '_blank');
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      
      {/* 0.00. FLAGSHIP INNOVATION 2026: DEEP BRAIN OS — АВТОНОМНА ОПЕРАЦІЙНА ОБВ'ЯЗКА ДЛЯ ШІ-АГЕНТІВ */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950/80 to-slate-950 border-2 border-emerald-500/60 hover:border-emerald-400 rounded-2xl p-5 sm:p-6 shadow-2xl overflow-hidden transition-all text-white">
        <div className="absolute top-0 right-0 -mr-12 -mt-12 w-80 h-80 bg-emerald-600/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-12 -mb-12 w-80 h-80 bg-teal-600/15 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2.5 max-w-3xl">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-emerald-500/30 text-emerald-200 border border-emerald-400/50 flex items-center gap-1.5 shadow-xs font-mono">
                <Cpu className="w-3.5 h-3.5 text-amber-300" />
                <span>🧠 {isPl ? 'DEEP BRAIN OS • AUTONOMICZNA POWŁOKA AI' : isEn ? 'DEEP BRAIN OS • AUTONOMOUS AGENT HARNESS' : 'DEEP BRAIN OS • АВТОНОМНА ОБВ\'ЯЗКА ДЛЯ ШІ-АГЕНТІВ'}</span>
              </span>
              <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/25 text-amber-300 border border-amber-400/50 flex items-center gap-1 font-mono animate-pulse">
                🔥 {isPl ? 'NOWOŚĆ 2026: 5 AGENTÓW' : isEn ? 'NEW 2026: 5-AGENT SOLVER' : 'НОВИНКА 2026: 5-АГЕНТНИЙ SOLVER'}
              </span>
              <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700 font-mono">
                {isPl ? 'WIECZNA PAMIĘĆ • ZERO CONTEXT ROT • 20+ SKILLS' : isEn ? 'PERSISTENT MEMORY • ZERO CONTEXT ROT • 20+ SKILLS' : 'ВІЧНА ПАМ\'ЯТЬ WIKI • ВАЛІДАЦІЯ КОДУ • 20+ НАВИЧОК • VARLOCK SHIELD'}
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight flex items-center gap-2">
              <span>
                {isPl 
                  ? 'Deep Brain OS: Przekształć agentów AI w Senior Inżyniera z pamięcią i testami' 
                  : isEn 
                  ? 'Deep Brain OS: Turn AI Agents into a Senior Engineer with Memory & Tests' 
                  : 'Deep Brain OS: Перетворіть ШІ-агентів на Senior-інженера з вічною пам\'яттю та тестами'}
              </span>
            </h2>

            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans font-medium">
              {isPl 
                ? 'Gotowa komercyjna powłoka operacyjna dla Google Antigravity i LLM: skompilowana pamięć Wiki eliminuje degradację kontekstu, 5-agentowy potok weryfikacji kodu (Planner -> Ideator A/B -> Solver -> Testy -> Reviewer z cofaniem Rollback) oraz 20+ gotowych umiejętności inżynierskich.'
                : isEn 
                ? 'Turnkey production harness for Google Antigravity & LLMs: write-time compiled LLM Wiki eliminates context rot, 5-agent ledger code verification pipeline with deterministic subprocess tests and atomic rollback, plus 20+ native engineering skills.'
                : 'Готова інженерна обв\'язка для Google Antigravity та LLM: скомпільована довгострокова пам\'ять (LLM Wiki) назавжди усуває втрату контексту (Context Rot), 5-агентний конвеєр перевірки коду (Planner -> паралельні Ideators -> Solver -> тести в субпроцесі -> відкат Rollback Guard), захист секретів Varlock Shield та 20+ перевірених навичок.'}
            </p>

            <div className="flex items-center gap-3 pt-1 text-xs font-mono text-emerald-300">
              <span>🛠️ Developer Kit: <strong>3 150 грн ($79)</strong></span>
              <span className="text-slate-600">•</span>
              <span>🚀 Cloud Server під ключ: <strong>3 550 грн/міс ($89)</strong></span>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap shrink-0">
            <button
              type="button"
              onClick={() => {
                setDeepBrainOsInitialTab('pricing');
                setShowDeepBrainOsModal(true);
              }}
              className="w-full sm:w-auto px-6 py-3.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-500 hover:from-emerald-500 hover:to-teal-400 text-white font-black text-sm rounded-xl shadow-xl shadow-emerald-900/40 transition-all flex items-center justify-center gap-2 cursor-pointer border border-emerald-400/50 group hover:scale-[1.02]"
            >
              <Sparkles className="w-4 h-4 text-amber-300 group-hover:rotate-12 transition-transform" />
              <span>{isPl ? '🚀 Kup licencję ($79) ➔' : isEn ? '🚀 Get License ($79) ➔' : '🚀 Придбати ліцензію ($79) ➔'}</span>
            </button>
            
            <button
              type="button"
              onClick={() => {
                setDeepBrainOsInitialTab('architecture');
                setShowDeepBrainOsModal(true);
              }}
              className="w-full sm:w-auto px-4 py-3.5 bg-slate-900/90 hover:bg-slate-800 text-emerald-300 hover:text-white font-bold text-sm rounded-xl border border-emerald-500/40 transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md"
            >
              <Info className="w-4 h-4 text-amber-300" />
              <span>{isPl ? 'ℹ️ Architektura & Demo' : isEn ? 'ℹ️ Architecture & Demo' : 'ℹ️ Архітектура та Демо'}</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* 0.0. FLAGSHIP SHOWCASE: ЄДИНИЙ КОРПОРАТИВНИЙ ЦИФРОВИЙ ОФІС ПІДПРИЄМСТВА */}
      <div className="relative bg-gradient-to-r from-slate-950 via-purple-950/90 to-slate-950 border-2 border-violet-500/60 hover:border-violet-400 rounded-2xl p-5 sm:p-6 shadow-2xl overflow-hidden transition-all text-white">
        <div className="absolute top-0 right-0 -mr-12 -mt-12 w-80 h-80 bg-violet-600/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-12 -mb-12 w-80 h-80 bg-indigo-600/15 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2.5 max-w-3xl">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-violet-500/30 text-violet-200 border border-violet-400/50 flex items-center gap-1.5 shadow-xs">
                <Building2 className="w-3.5 h-3.5 text-amber-300" />
                <span>{isPl ? '🏢 KORPORACYJNE BIURO CYFROWE' : isEn ? '🏢 CORPORATE DIGITAL OFFICE' : '🏢 КОРПОРАТИВНИЙ ЦИФРОВИЙ ОФІС'}</span>
              </span>
              <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/25 text-amber-300 border border-amber-400/50 flex items-center gap-1 font-mono animate-pulse">
                🔥 {isPl ? '3 DNI ZA DARMO (FREE TRIAL)' : isEn ? '3 DAYS FREE TRIAL' : '3 ДНІ БЕЗКОШТОВНО (FREE TRIAL)'}
              </span>
              {(hasEnterprise || enterpriseName) && (
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-400/40 flex items-center gap-1 font-mono">
                  <ShieldCheck className="w-3 h-3 text-blue-400" />
                  <span>{enterpriseName || 'НЦУВКЗ'}{isEnterpriseAdmin ? ' (Адмін)' : ''}</span>
                </span>
              )}
              <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-800 text-slate-300 border border-slate-700 font-mono">
                {isPl ? 'ДСТУ 4163:2020 • WIKI • 40+ MODUŁÓW • E-AKTY' : isEn ? 'DSTU 4163:2020 • WIKI • 40+ MODULES • PRIMARY ACTS' : 'ДСТУ 4163:2020 • БАЗА ЗНАНЬ WIKI • 40+ МОДУЛІВ • ЄДРПОУ & ПЕРВИННІ АКТИ'}
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight flex items-center gap-2">
              <span>
                {enterpriseName 
                  ? `${isPl ? 'Korporacyjne Biuro Cyfrowe' : isEn ? 'Corporate Digital Office' : 'Корпоративний Цифровий Офіс'}: ${enterpriseName}`
                  : (isPl ? 'Korporacyjne Biuro Cyfrowe Przedsiębiorstwa' : isEn ? 'Corporate Digital Office for Enterprise' : 'Корпоративний Цифровий Офіс Підприємства')}
              </span>
            </h2>

            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans font-medium">
              {isPl 
                ? 'Zintegrowana platforma operacyjna i administracyjna: inteligentny asystent zarządu, automatyczne generowanie dokumentów Word (.docx) wg DSTU 4163:2020, arkusze Excel (.xlsx) z żywymi formułami, strukturalna baza wiedzy Wiki, 40+ modułów branżowych, profile NIP/REGON, szablony pism i comiesięczne akty rozliczeniowe.'
                : isEn 
                ? 'Unified operational workspace & enterprise management: AI executive workspace, automated Word (.docx) generation under DSTU 4163:2020 with 1-click download, Excel sheets with live formulas, persistent company Wiki, 40+ modules, tax profiles, branded letterheads, and official primary accounting acts.'
                : 'Єдиний корпоративний центр управління та діловодства: структурована база знань WIKI, щоденний робочий чат, автоматична генерація наказів, довідок, договорів та актів у Word (.docx) за ДСТУ 4163:2020 з миттєвим завантаженням, живі формули Excel, понад 40 галузевих модулів, реквізити ЄДРПОУ, фірмові бланки та первинні розрахункові акти.'}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap shrink-0">
            <button
              type="button"
              onClick={() => {
                if (onOpenEnterpriseDashboard) onOpenEnterpriseDashboard();
                else if (onOpenCorporateOffice) onOpenCorporateOffice();
              }}
              className="w-full sm:w-auto px-6 py-3.5 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-black text-sm rounded-xl shadow-xl shadow-violet-600/30 transition-all flex items-center justify-center gap-2 cursor-pointer border border-violet-400/50 group hover:scale-[1.02]"
            >
              <Sparkles className="w-4 h-4 text-amber-300 group-hover:rotate-12 transition-transform" />
              <span>{isPl ? '🚀 Wejdź do Biura Cyfrowego ➔' : isEn ? '🚀 Open Digital Office ➔' : '🚀 Увійти в Цифровий Офіс ➔'}</span>
            </button>
            
            <button
              type="button"
              onClick={() => onOpenCorporateOfficeInfo && onOpenCorporateOfficeInfo()}
              className="w-full sm:w-auto px-4 py-3.5 bg-slate-900/90 hover:bg-slate-800 text-violet-300 hover:text-white font-bold text-sm rounded-xl border border-violet-500/40 transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md"
            >
              <Info className="w-4 h-4 text-amber-300" />
              <span>{isPl ? 'ℹ️ Jak to działa' : isEn ? 'ℹ️ How it works' : 'ℹ️ Про систему / Як це працює'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 1. TOP SECTION: HERO PROMO BANNER (ІНТЕЛЕКТУАЛЬНИЙ КОМАНДНИЙ ЦЕНТР) */}
      <div className="relative bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 overflow-hidden shadow-xs">
        {/* Background decorative gradients */}
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-80 h-80 bg-red-500/[0.04] rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-80 h-85 bg-blue-500/[0.03] rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl space-y-4 font-mono">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold rounded bg-red-50 text-red-600 border border-red-200">
            <Sparkles className="w-3.5 h-3.5 text-red-500 animate-pulse" />
            <span>
              {isDe 
                ? 'ZENTRUM FÜR AUTOMATISIERUNG & DOKUMENTENMANAGEMENT DEEP RESEARCH HUB' 
                : isFr 
                ? 'CENTRE D\'AUTOMATISATION ET DE GESTION DOCUMENTAIRE DEEP RESEARCH HUB' 
                : isPl 
                ? 'CENTRUM AUTOMATYZACJI I OBIEGU DOKUMENTÓW DEEP RESEARCH HUB' 
                : isUk 
                ? 'ЦЕНТР АВТОМАТИЗАЦІЇ РУТИНИ ТА ДОКУМЕНТООБІГУ DEEP RESEARCH HUB' 
                : 'UNIFIED AI AGENT ECOSYSTEM DEEP RESEARCH HUB'}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h1 className="text-xl sm:text-3xl font-extrabold text-slate-900 uppercase tracking-tight leading-snug">
              {isDe
                ? 'ZENTRUM FÜR AUTOMATISIERUNG & DOKUMENTENMANAGEMENT'
                : isFr
                ? 'CENTRE OPÉRATIONNEL D\'AUTOMATISATION & DE DOCUMENTATION'
                : isPl 
                ? 'CENTRUM AUTOMATYZACJI I OBIEGU DOKUMENTÓW'
                : isUk 
                ? 'ЦЕНТР АВТОМАТИЗАЦІЇ РУТИНИ ТА ДОКУМЕНТООБІГУ' 
                : 'AUTOMATION & DOCUMENTATION COMMAND CENTER'}
            </h1>
            <button
              onClick={() => {
                setIsFeedbackOpen(true);
                setFeedbackSuccess(false);
                setFeedbackError(null);
              }}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase rounded-xl transition-colors cursor-pointer shrink-0 shadow-xs flex items-center gap-1.5"
            >
              <MessageSquare className="w-4 h-4" />
              <span>{isDe ? 'Feedback / Kontakt' : isFr ? 'Avis / Contact' : isPl ? 'Opinia / Kontakt' : isUk ? 'Зворотній зв\'язок' : 'Feedback'}</span>
            </button>
          </div>

          <p className="text-xs text-slate-600 leading-relaxed font-sans font-medium">
            {isDe
              ? 'Sparen Sie bis zu 80 % Ihrer Arbeitszeit und schützen Sie Ihr Unternehmen vor steuerlichen, rechtlichen und finanziellen Risiken. Erhalten Sie fertige Kostenschätzungen, geprüfte Verträge, Steuererklärungen und analytische Berichte im nativen Word- und Excel-Format in 3 Minuten statt Tagen mühsamer Handarbeit.'
              : isFr
              ? 'Économisez jusqu\'à 80 % de votre temps de travail et protégez votre entreprise contre les risques fiscaux, juridiques et financiers. Obtenez des devis prêts à l\'emploi, des contrats vérifiés, des déclarations fiscales et des rapports analytiques aux formats natifs Word et Excel en 3 minutes au lieu de jours de travail fastidieux.'
              : isPl 
              ? 'Zaoszczędź do 80% czasu pracy i zabezpiecz firmę przed ryzykiem podatkowym, prawnym i finansowym. Otrzymuj gotowe kosztorysy, zweryfikowane umowy, deklaracje podatkowe i raporty w natywnym formacie Word i Excel w 3 minuty zamiast dni żmudnej pracy.'
              : isUk 
              ? 'Звільніть до 80% робочого часу та захистіть бізнес від податкових, юридичних і фінансових ризиків. Отримуйте готові кошториси, перевірені договори, податкові декларації та аналітичні звіти у нативному Word та Excel за 3 хвилини замість днів виснажливої ручної роботи.'
              : 'Save up to 80% of working hours and protect your business from tax, legal, and financial risks. Receive ready-to-use cost estimates, verified contracts, tax declarations, and reports in native Word and Excel in 3 minutes.'}
          </p>

          <div className="bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-indigo-500/10 border border-emerald-500/30 rounded-xl p-4 sm:p-5 space-y-2 shadow-xs">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-emerald-800 font-extrabold text-xs uppercase tracking-wider">
                <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
                <span>
                  {isDe 
                    ? '🎁 SIE HABEN KOSTENLOSE TESTANFRAGEN!' 
                    : isFr 
                    ? '🎁 VOUS AVEZ DES REQUÊTES D\'ESSAI GRATUITES !' 
                    : isPl 
                    ? '🎁 MASZ DARMOWE TESTOWE URUCHOMIENIA!' 
                    : isUk 
                    ? '🎁 ВАМ НАРАХОВАНО БЕЗКОШТОВНІ ТЕСТОВІ ЗАПУСКИ!' 
                    : '🎁 YOU HAVE FREE TRIAL REQUESTS AVAILABLE!'}
                </span>
              </div>
              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-emerald-600 text-white uppercase shadow-xs">
                {isDe ? 'Ohne Kreditkarte' : isFr ? 'Sans carte bancaire' : isPl ? 'Bez podawania karty' : isUk ? 'Без прив\'язки картки' : 'No credit card needed'}
              </span>
            </div>
            <p className="text-slate-700 leading-relaxed text-xs">
              {isDe
                ? 'Sie können jedes Werkzeug im Ökosystem kostenlos testen (1 Gratis-Anfrage pro Modul). Guthabenaufladung oder Bankkarte sind NICHT erforderlich!'
                : isFr
                ? 'Vous pouvez tester gratuitement chaque outil de l\'écosystème (1 requête d\'essai par module). Aucun rechargement de solde ni carte bancaire n\'est requis !'
                : isPl 
                ? 'Możesz bezpłatnie przetestować każde narzędzie ekosystemu (po 1 darmowym zapytaniu na każdy moduł). Doładowanie salda ani karta NIE są wymagane!'
                : isUk 
                ? 'Ви можете безкоштовно випробувати кожен інструмент екосистеми (по 1 безкоштовному запиту на кожен модуль). Поповнення балансу чи введення банківської картки НЕ потрібні!'
                : 'You can test each tool for free (1 trial request per tool). No top-up or credit card required!'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 text-[10px] text-slate-600">
            <div className="flex items-center gap-2 bg-slate-50 p-2.5 rounded-lg border border-slate-200 font-bold">
              <Cpu className="w-4 h-4 text-emerald-600" />
              <span>{isDe ? '⏱️ Bis zu 80 % Zeitersparnis' : isFr ? '⏱️ Jusqu\'à 80 % de temps gagné' : isPl ? '⏱️ Do 80% oszczędności czasu' : isUk ? '⏱️ Економія до 80% часу' : '⏱️ Save up to 80% time'}</span>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 p-2.5 rounded-lg border border-slate-200 font-bold">
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              <span>{isDe ? '🛡️ 100 % Risikoschutz' : isFr ? '🛡️ 100 % Protection des risques' : isPl ? '🛡️ 100% Ochrona przed ryzykiem' : isUk ? '🛡️ 100% Захист від ризиків' : '🛡️ 100% Risk protection'}</span>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 p-2.5 rounded-lg border border-slate-200 font-bold">
              <Database className="w-4 h-4 text-indigo-600" />
              <span>{isDe ? '📥 Fertige .DOCX/.XLSX in 3 Min.' : isFr ? '📥 .DOCX/.XLSX prêts en 3 min' : isPl ? '📥 Gotowe .DOCX/.XLSX w 3 min' : isUk ? '📥 Готові .DOCX/.XLSX за 3 хв' : '📥 Ready .DOCX/.XLSX in 3 min'}</span>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-3.5 flex items-center justify-between gap-3 flex-wrap text-slate-300 text-xs mt-3">
            <div className="flex items-center gap-2 font-bold text-slate-200">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>{isDe ? '🔒 100 % private Umgebung (kein Modelltraining)' : isFr ? '🔒 100 % Espace privé (aucun entraînement des modèles)' : isPl ? '🔒 100% Prywatne środowisko (brak douczania modeli)' : isUk ? '🔒 100% Приватний контур (без донавчання моделей)' : '🔒 100% Private contour (no model training)'}</span>
            </div>
            <div className="flex items-center gap-2 font-bold text-slate-200">
              <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" />
              <span>{isDe ? '⚖️ Konformität mit gesetzlichen Standards (EU / DIN)' : isFr ? '⚖️ Conformité réglementaire et normes légales' : isPl ? '⚖️ Pełna zgodność ze standardami i prawem' : isUk ? '⚖️ Нормативи ДСТУ, ПКУ, ДБН & ДССЗЗІ' : '⚖️ Compliant with State Standards'}</span>
            </div>
            <div className="flex items-center gap-2 font-bold text-slate-200">
              <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
              <span>{isDe ? '🧑‍💼 Human-in-the-Loop-Prinzip (menschliche Kontrolle)' : isFr ? '🧑‍💼 Principe Human-in-the-Loop (contrôle humain)' : isPl ? '🧑‍💼 Zasada Human-in-the-Loop (kontrola przez człowieka)' : isUk ? '🧑‍💼 Принцип Human-in-the-Loop (контроль людиною)' : '🧑‍💼 Human-in-the-Loop principle'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 1.0 SMART AI AGENT DISPATCHER & NAVIGATOR */}
      <SmartAgentDispatcher 
        onLaunchAgent={onLaunchAgent} 
        language={language} 
        currentUserEmail={currentUserEmail}
        enterpriseName={enterpriseName}
        hasEnterprise={hasEnterprise}
        onOpenEnterpriseDashboard={onOpenEnterpriseDashboard}
      />

      {/* 1.0.0 ОПЕРАТИВНИЙ СИТУАЦІЙНИЙ ЦЕНТР ТА КАРТИ ЗАГРОЗ / «НІС-ЕКО» (НА ГОЛОВНІЙ СТОРІНЦІ) */}
      <div id="main-page-situational-maps" className="scroll-mt-6 animate-fade-in">
        <SituationalCenterShowcase 
          language={language} 
          onSelectAgent={(agentId) => onLaunchAgent(agentId)}
          onOpenPricing={onOpenPricing} 
          isLoggedIn={true}
          currentUserEmail={currentUserEmail}
        />
      </div>

      {/* 1.0.1 ADMIN EXCLUSIVE MODULES BANNER */}
      {isAdmin && (
        <div className="bg-gradient-to-r from-amber-950 via-slate-900 to-slate-950 border-2 border-amber-500/60 rounded-2xl p-6 shadow-xl text-white font-mono space-y-4 animate-fade-in">
          <div className="flex items-center justify-between flex-wrap gap-2 border-b border-amber-500/30 pb-3">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-400" />
              <h2 className="text-sm font-extrabold uppercase tracking-wide text-amber-300">
                👑 {isDe ? 'SPEZIELLE ADMIN-MODULE (ADMIN EXCLUSIVE)' : isFr ? 'MODULES SPÉCIAUX ADMINISTRATEUR (ADMIN EXCLUSIF)' : isPl ? 'SPECJALNE MODUŁY ADMINISTRATORA (ADMIN EXCLUSIVE)' : isUk ? 'СПЕЦМОДУЛІ АДМІНІСТРАТОРА (ADMIN EXCLUSIVE)' : 'ADMIN EXCLUSIVE SPECIAL MODULES'}
              </h2>
            </div>
            <span className="text-[10px] font-bold px-2.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-400/40">
              {isDe ? '🛡️ Nur für Administratoren' : isFr ? '🛡️ Réservé aux Administrateurs' : isPl ? '🛡️ Tylko dla Administratorów' : isUk ? '🛡️ Тільки для irinacosmos777 & bbass776' : '🛡️ Only for Admins'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Folk Healer Card */}
            <div className="bg-slate-900/90 border border-amber-500/50 rounded-xl p-5 flex flex-col justify-between gap-4 hover:border-amber-400 transition-all shadow-md">
              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-extrabold uppercase px-2.5 py-0.5 bg-amber-500/20 text-amber-300 rounded border border-amber-400/30 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    {isDe ? 'Wahrsagen • Kräuter • Wachs • Kaffee • Bräuche' : isFr ? 'Divination • Herboristerie • Cire • Café • Traditions' : isPl ? 'Wróżenie • Zioła • Wosk • Kawa • Tradycje' : isUk ? 'Ворожіння • Трави • Воск • Кава • Традиції' : 'Divination • Herbs • Wax • Coffee • Rites'}
                  </span>
                  <span className="text-[9px] font-bold text-amber-400 bg-amber-950 px-2 py-0.5 rounded border border-amber-500/30">
                    Gemini 3.7 Flash & Vision
                  </span>
                </div>
                <h3 className="text-sm font-extrabold text-amber-100 uppercase tracking-tight flex items-center gap-2">
                  <span>🔮 {isDe ? 'Experte für Volksheilkunde, Bräuche & Orakel' : isFr ? 'Expert en Guérison Traditionnelle, Plantes & Divination' : isPl ? 'Ekspert Ziołolecznictwa, Tradycji & Wróżenia' : isUk ? 'Експерт Травництва & Традицій, Травництва & Ворожіння' : 'Folk Healing, Herbalism & Traditions Oracle'}</span>
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  {isDe
                    ? 'Automatisiertes Multi-Agenten-System mit Kamera und Gemini 3.7 Flash & Vision zur Analyse von Wachs, Kaffeesatz, Kräutern und Ritualen mit Word (.docx)-Export.'
                    : isFr
                    ? 'Système multi-agents automatisé avec caméra et Gemini 3.7 Flash & Vision pour l\'analyse de cire, marc de café, herbes et rituels avec export Word (.docx).'
                    : isPl
                    ? 'Zautomatyzowany kompleks z użyciem kamery i Gemini 3.7 Flash & Vision do analizy wosku, fusów kawowych, ziół i obrzędów z eksportem do Word (.docx).'
                    : isUk 
                    ? 'Автоматизований комплекс із використанням фотокамери та Gemini 3.7 Flash & Vision для аналізу воску, кавової гущі, трав, обрядів та ворожінь з експортом у Word (.docx).'
                    : 'Multi-agent AI complex with live camera & Gemini 3.7 Flash & Vision for wax casting, coffee grounds reading, herbs, and divination with Word (.docx) export.'}
                </p>
              </div>
              <button
                onClick={() => onLaunchAgent('folk-healer-traditions')}
                className="w-full py-3 bg-amber-600 hover:bg-amber-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-amber-600/30 hover:scale-[1.01]"
              >
                <span>{isDe ? '🔮 Modul für Traditionen & Heilung öffnen' : isFr ? '🔮 Ouvrir le Module Traditions & Guérison' : isPl ? '🔮 Otwórz Moduł Tradycji & Uzdrawiania' : isUk ? '🔮 Відкрити Модуль Ворожіння & Цілительства' : '🔮 Open Divination & Healing Module'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Admin Lead Harvester Card */}
            <div className="bg-slate-900/90 border border-red-500/50 rounded-xl p-5 flex flex-col justify-between gap-4 hover:border-red-400 transition-all shadow-md">
              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-extrabold uppercase px-2.5 py-0.5 bg-red-500/20 text-red-300 rounded border border-red-400/30 flex items-center gap-1">
                    <UserCheck className="w-3.5 h-3.5 text-red-400" />
                    {isDe ? 'OSINT • Gmail-Harvester • Foren' : isFr ? 'OSINT • Collecteur Gmail • Forums' : isPl ? 'OSINT • Gmail Harvester • Fora' : isUk ? 'OSINT • Gmail Harvester • Форуми' : 'OSINT • Gmail Harvester • Forums'}
                  </span>
                  <span className="text-[9px] font-bold text-red-400 bg-red-950 px-2 py-0.5 rounded border border-red-500/30">
                    Admin Tool
                  </span>
                </div>
                <h3 className="text-sm font-extrabold text-red-100 uppercase tracking-tight flex items-center gap-2">
                  <span>🎯 {isDe ? 'Gmail & Foren Kontakt-Harvester (Admin Lead Harvester)' : isFr ? 'Collecteur de Contacts Gmail & Forums (Admin Lead Harvester)' : isPl ? 'Pozyskiwanie Kontaktów Gmail & Forów (Lead Harvester)' : isUk ? 'Парсер Контактів Gmail & Форумів (Admin Lead Harvester)' : 'AI Gmail & Forum Lead Harvester'}</span>
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  {isDe
                    ? 'Spezielles Administrator-Tool zur gezielten Suche nach öffentlichen Gmail-Adressen von Fachleuten (Buchhalter, Juristen) und thematischen Webforen.'
                    : isFr
                    ? 'Outil d\'administration exclusif pour la collecte ciblée d\'adresses Gmail publiques de professionnels (experts-comptables, juristes) et de forums spécialisés.'
                    : isPl
                    ? 'Specjalne narzędzie administratora do wyszukiwania publicznych adresów Gmail specjalistów (księgowi, prawnicy) oraz forów branżowych.'
                    : isUk 
                    ? 'Спеціальний інструмент адміністратора для пошуку публічних Gmail-пошт фахівців (бухгалтери, юристи) та тематичних веб-форумів в Україні.'
                    : 'Admin-exclusive tool for harvesting public Gmail addresses of targeted professionals and specialized web forum URLs.'}
                </p>
              </div>
              <button
                onClick={() => onLaunchAgent('admin-lead-harvester')}
                className="w-full py-3 bg-red-600 hover:bg-red-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-red-600/30 hover:scale-[1.01]"
              >
                <span>{isDe ? '🎯 Lead-Harvester öffnen' : isFr ? '🎯 Ouvrir le Collecteur de Contacts' : isPl ? '🎯 Otwórz Pozyskiwanie Kontaktów' : isUk ? '🎯 Відкрити Парсер Контактів' : '🎯 Open Lead Harvester'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 1.1 FEATURED PRIMARY MODULE: КАБІНЕТ ПІДПРИЄМСТВА & AI TO-DO */}
      {enterpriseAgent && (
        <div className="relative bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 border-2 border-blue-500/70 hover:border-blue-400 rounded-2xl p-6 sm:p-8 text-white shadow-2xl shadow-blue-500/20 overflow-hidden flex flex-col md:flex-row md:items-center justify-between gap-6 font-mono transition-all">
          <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute left-1/4 bottom-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />

          <div className="space-y-3.5 z-10 max-w-3xl">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1 text-[9px] font-black uppercase bg-blue-500/30 text-blue-300 border border-blue-400/50 rounded-lg flex items-center gap-1.5 shadow-xs">
                <Briefcase className="w-3.5 h-3.5 text-blue-400" />
                <span>{isDe ? '⭐ UNTERNEHMENS-HAUPTMODUL' : isFr ? '⭐ MODULE PRINCIPAL ENTREPRISE' : isPl ? '⭐ GŁÓWNY MODUŁ PRZEDSIĘBIORSTWA' : isUk ? '⭐ ГОЛОВНИЙ МОДУЛЬ ПІДПРИЄМСТВА' : '⭐ PRIMARY ENTERPRISE MODULE'}</span>
              </span>
              <span className="px-3 py-1 text-[9px] font-black uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/50 rounded-lg flex items-center gap-1.5 shadow-xs">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span>{isDe ? '100 % KOSTENLOSER ZUGANG (FREE)' : isFr ? 'ACCÈS 100% LIBRE (FREE)' : isPl ? '100% WOLNY DOSTĘP (FREE)' : isUk ? '100% ВІЛЬНИЙ ДОСТУП (FREE)' : '100% FREE ACCESS'}</span>
              </span>
              <span className="px-3 py-1 text-[9px] font-black uppercase bg-purple-500/30 text-purple-300 border border-purple-400/50 rounded-lg flex items-center gap-1.5 shadow-xs">
                <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                <span>{isDe ? 'AI TO-DO • UNTERNEHMENSPROFIL • ANALYTIK' : isFr ? 'AI TO-DO • PROFIL ENTREPRISE • ANALYTIQUE' : isPl ? 'AI TO-DO • PROFILE FIRMY • STATYSTYKA' : isUk ? 'AI TO-DO • ПРОФІЛЬ КОМПАНІЇ • АНАЛІТИКА' : 'AI TO-DO • COMPANY PROFILE'}</span>
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black uppercase text-white flex items-center gap-3 tracking-tight">
              <div className="p-2.5 bg-blue-600/20 border border-blue-400/40 rounded-xl text-blue-400 shadow-inner shrink-0">
                <Briefcase className="w-6 h-6" />
              </div>
              <span className="bg-gradient-to-r from-white via-slate-100 to-blue-200 bg-clip-text text-transparent">
                {getLocalizedAgentName(enterpriseAgent, language)}
              </span>
            </h2>

            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono font-medium">
              {getLocalizedAgentDesc(enterpriseAgent, language)}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 pt-2">
              {getLocalizedAgentFeatures(enterpriseAgent, language).map((f: string, i: number) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-200 font-bold bg-slate-900/80 border border-slate-800 px-3 py-1.5 rounded-lg shadow-xs">
                  <span className="w-2 h-2 rounded-full bg-blue-400 shadow-xs shadow-blue-400 shrink-0 animate-pulse" />
                  <span className="truncate">{f}</span>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => handleLaunchAgent(enterpriseAgent)}
            className="px-7 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-black text-xs uppercase rounded-xl transition-all shadow-xl shadow-blue-600/40 flex items-center justify-center gap-3 cursor-pointer shrink-0 z-10 hover:scale-[1.02] border border-blue-400/30"
          >
            <span>{isDe ? '🚀 UNTERNEHMENSBEREICH ÖFFNEN' : isFr ? '🚀 OUVRIR L\'ESPACE ENTREPRISE' : isPl ? '🚀 OTWÓRZ PANEL PRZEDSIĘBIORSTWA' : isUk ? '🚀 ВІДКРИТИ КАБІНЕТ ПІДПРИЄМСТВА' : '🚀 OPEN ENTERPRISE DASHBOARD'}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 2. MIDDLE SECTION: COLLAPSIBLE OPERATIONAL HUBS */}
      <div className="space-y-4 pt-2 font-mono">
        <div className="flex items-center justify-between p-4 sm:p-5 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border-2 border-indigo-500/40 rounded-2xl shadow-xl flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-600/30 border border-indigo-400/40 rounded-xl text-indigo-300 shrink-0">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm sm:text-base font-extrabold uppercase text-white tracking-wide">
                  {isDe 
                    ? '📂 14 SPEZIALISIERTE OPERATIONSZENTREN & BRANCHEN-HUBS' 
                    : isFr 
                    ? '📂 14 HUBS ET CENTRES OPÉRATIONNELS SPÉCIALISÉS' 
                    : isPl 
                    ? '📂 14 WYSPECJALIZOWANYCH CENTRÓW I HUBÓW OPERACYJNYCH' 
                    : isUk 
                    ? '📂 14 ГАЛУЗЕВИХ ТА РЕГІОНАЛЬНИХ ОПЕРАЦІЙНИХ ЦЕНТРІВ' 
                    : '📂 14 SPECIALIZED OPERATIONAL HUBS'}
                </h2>
                <span className="px-2 py-0.5 text-[9px] bg-indigo-500/30 text-indigo-300 border border-indigo-400/40 rounded font-bold uppercase">
                  {isUk ? '14 Центрів • 326+ Модулів' : '14 Hubs • 326+ Agents'}
                </span>
              </div>
              <p className="text-xs text-slate-300 mt-0.5">
                {isUk 
                  ? 'Україна, Польща, ЄС, США, Франція, Німеччина, Будівництво, Енергетика, Податки, Агро, Кібер, Космос, Наука, Фармація' 
                  : 'Ukraine, Poland, EU, US, France, Germany, Construction, Energy, Taxes, Agri, Cyber, Space, Science, Pharma'}
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsCentersExpanded(!isCentersExpanded)}
            className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer border border-indigo-400/30 hover:scale-[1.02]"
          >
            <span>{isCentersExpanded ? (isUk ? '📁 Згорнути Центри' : isPl ? '📁 Zwiń Centra' : '📁 Collapse Hubs') : (isUk ? '📂 Показати всі 14 Центрів' : isPl ? '📂 Rozwiń 14 Centrów' : '📂 Expand 14 Hubs')}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isCentersExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Collapsible Centers Grid */}
        {isCentersExpanded && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5 animate-fade-in">
          {/* Ukraine Dedicated National Operational Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-blue-950 to-amber-950 border-2 border-amber-500/60 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/30 text-amber-300 border border-amber-400/40 rounded flex items-center gap-1">
                  <span>🇺🇦</span>
                  <span>ДСТУ • ПКУ • PROZORRO • АВК-5 • ДЗК</span>
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
                  23 Модулі
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-amber-600/30 text-amber-200 border border-amber-400/30 rounded">
                  14,900 грн/міс
                </span>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-amber-200 flex items-center gap-2">
                  <span>🇺🇦 НАЦІОНАЛЬНИЙ ЦЕНТР УКРАЇНИ</span>
                </h3>
                <div className="text-[10px] font-bold text-amber-400/90 uppercase tracking-wider mt-0.5">
                  Правовий, Податковий, Будівельний & Земельний Комплаєнс
                </div>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Повний комплекс прикладних рішень для бізнесу в Україні: договори за ЦКУ, ПКУ & ДПС звітність (ПДВ, ЄП, КЕКВ), кошториси АВК-5 (КБ-2в/КБ-3) за ДБН, тендери Prozorro, АМКУ та земельний кадастр ДЗК.
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('ukraine-center-hub', 'uk')}
              className="px-5 py-3.5 bg-gradient-to-r from-blue-600 to-amber-600 hover:from-blue-500 hover:to-amber-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-amber-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>🇺🇦 Відкрити Центр України (23)</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Poland Dedicated Operational Hub Banner (Always Native Polish on Main Page) */}
          <div className="relative bg-gradient-to-r from-slate-950 via-rose-950 to-slate-900 border-2 border-rose-500/60 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-rose-500/30 text-rose-300 border border-rose-400/40 rounded flex items-center gap-1">
                  <span>🇵🇱</span>
                  <span>KSeF • RODO • Karta Pobytu • BDO • KPO</span>
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  {isDe ? '18 Experten-Module' : isFr ? '18 Modules Experts' : '18 Modułów Eksperckich'}
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-rose-600/30 text-rose-200 border border-rose-400/30 rounded">
                  1,490 PLN/msc
                </span>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-rose-200 flex items-center gap-2">
                  <span>🇵🇱 CENTRUM OPERACYJNE POLSKA</span>
                </h3>
                <div className="text-[10px] font-bold text-rose-400/90 uppercase tracking-wider mt-0.5">
                  Polski Hub Biznesowy, Prawny, Podatkowy & ZUS
                </div>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Dedykowany ekosystem narzędzi dla firm w Polsce: KSeF FA_VAT, Biała Lista & MPP, Karta Pobytu cudzoziemców, audyt RODO/UODO, restrukturyzacja i ochrona Zarządu, rejestr BDO, dotacje KPO/FENG, schematy MDR, Ceny Transferowe TP, Ulga B+R, B2B vs PIP, ZUS A1 i kosztorysy KNR.
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('poland-center-hub', 'pl')}
              className="px-5 py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-rose-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🇵🇱 Polen-Zentrum öffnen (18)' : isFr ? '🇵🇱 Ouvrir le Centre Pologne (18)' : '🇵🇱 Otwórz Centrum Polska (18)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* EU Dedicated Operational Hub Banner (Always Native English on Main Page) */}
          <div className="relative bg-gradient-to-r from-slate-950 via-blue-950 to-indigo-950 border-2 border-blue-500/60 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded flex items-center gap-1">
                  <span>🇪🇺</span>
                  <span>TED • CBAM • DORA • DAC7 • REACH • MDR</span>
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/30 text-amber-300 border border-amber-400/40 rounded">
                  {isDe ? '18 EU-Module' : isFr ? '18 Modules UE' : '18 EU Modules'}
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-blue-600/30 text-blue-200 border border-blue-400/30 rounded">
                  €399/mo
                </span>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-blue-200 flex items-center gap-2">
                  <span>🇪🇺 EU ENTERPRISE & REGULATORY HUB</span>
                </h3>
                <div className="text-[10px] font-bold text-blue-400/90 uppercase tracking-wider mt-0.5">
                  Cross-Border Single Market, Directives & ESG Suite
                </div>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Specialized compliance suite for the EU Single Market: TED procurement & ESPD XML, CBAM & CSRD ESG carbon reporting, DORA financial ICT resilience, DAC7 marketplace tax, REACH/CLP chemical SDS, MDR medical devices, EUDR Deforestation TRACES, EU AI Act, NIS2 Cybersecurity, Mobility Package, and Customs UCC.
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('eu-center-hub', 'en')}
              className="px-5 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🇪🇺 EU-Hub öffnen (18)' : isFr ? '🇪🇺 Ouvrir le Hub UE (18)' : '🇪🇺 Open EU Enterprise Hub (18)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {/* US Dedicated Operational Hub Banner (Native English on Main Page) */}
          <div className="relative bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 border-2 border-blue-500/60 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-rose-500/30 text-rose-300 border border-rose-400/40 rounded flex items-center gap-1">
                  <span>🇺🇸</span>
                  <span>DELAWARE • FAR • IRS • OFAC • SOC 2 • FDA</span>
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
                  {isDe ? '14 US-Module' : isFr ? '14 Modules USA' : '14 US Modules'}
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-rose-600/30 text-rose-200 border border-rose-400/30 rounded">
                  $399/mo
                </span>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-blue-200 flex items-center gap-2">
                  <span>🇺🇸 US ENTERPRISE & COMPLIANCE HUB</span>
                </h3>
                <div className="text-[10px] font-bold text-rose-400/90 uppercase tracking-wider mt-0.5">
                  Federal & State Corporate, Tax & Procurement Suite
                </div>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Specialized operating suite for entering and running businesses in the US: Delaware LLC & C-Corp incorporation, YC Post-Money SAFE, FinCEN BOI (CTA) reporting, SAM.gov FAR proposals, IRS Form 5472, Sales Tax Nexus, OFAC/ITAR sanctions, MSA contracts, HIPAA, and SOC 2 Type II.
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('us-center-hub', 'en')}
              className="px-5 py-3.5 bg-rose-600 hover:bg-rose-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-rose-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🇺🇸 US-Hub öffnen (14)' : isFr ? '🇺🇸 Ouvrir le Hub USA (14)' : '🇺🇸 Open US Enterprise Hub (14)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* France Dedicated Operational Hub Banner (Always Native French on Main Page) */}
          <div className="relative bg-gradient-to-r from-slate-950 via-blue-950 to-rose-950 border-2 border-blue-500/60 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded flex items-center gap-1">
                  <span>🇫🇷</span>
                  <span>FACTUR-X • INPI • URSSAF • RE2020 • BOAMP • CNIL</span>
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  {isDe ? '8 Experten-Module' : '8 Modules Experts'}
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-rose-600/30 text-rose-200 border border-rose-400/30 rounded">
                  €399/mois
                </span>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-blue-200 flex items-center gap-2">
                  <span>🇫🇷 CENTRE OPÉRATIONNEL FRANCE (HUB FRANCE)</span>
                </h3>
                <div className="text-[10px] font-bold text-rose-300/90 uppercase tracking-wider mt-0.5">
                  Code de Commerce, Factur-X, URSSAF & RE2020 Compliance
                </div>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Écosystème d'entreprise dédié au marché français : e-facturation Factur-X & Chorus Pro / PPF, statuts SAS/SARL/SCI auprès de l'INPI Guichet Unique, conformité sociale Code du travail & URSSAF, déclarations préalables SIPSI, BTP RE2020 & CCAG Travaux, marchés publics BOAMP / PLACE, baux commerciaux et Bilan Carbone ADEME.
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('france-center-hub', 'fr')}
              className="px-5 py-3.5 bg-gradient-to-r from-blue-600 to-rose-600 hover:from-blue-500 hover:to-rose-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🇫🇷 Frankreich-Zentrum öffnen (8)' : '🇫🇷 Ouvrir le Centre France (8)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Germany & DACH Enterprise & Regulatory Center Hub Banner (Always Native German on Main Page) */}
          <div className="relative bg-gradient-to-r from-slate-950 via-amber-950 to-rose-950 border-2 border-amber-500/60 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/30 text-amber-300 border border-amber-400/40 rounded flex items-center gap-1">
                  <span>🇩🇪</span>
                  <span>XRECHNUNG • GMBH • VOB/B • HOAI • LKSG • MILOG • DSGVO</span>
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  {isFr ? '8 Modules Experts' : '8 Experten-Module'}
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-amber-600/30 text-amber-200 border border-amber-400/30 rounded">
                  €399/Monat
                </span>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-amber-200 flex items-center gap-2">
                  <span>🇩🇪 DEUTSCHES OPERATIONSZENTRUM (HUB DEUTSCHLAND)</span>
                </h3>
                <div className="text-[10px] font-bold text-amber-300/90 uppercase tracking-wider mt-0.5">
                  BGB, HGB, XRechnung, VOB/B, HOAI & LkSG Compliance
                </div>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Dediziertes Betriebssystem für den deutschen Markt (DACH): E-Rechnungen nach XRechnung & ZUGFeRD 2.2, Gesellschaftsverträge GmbH/UG zum Handelsregister, Bauvertragsrecht VOB/B & HOAI 2021, Lieferkettengesetz LkSG & BAFA, Mindestlohn MiLoG & A1-Entsendung, öffentliche Ausschreibungen Vergabe.de und DSGVO/BDSG-Audits.
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('germany-center-hub', 'de')}
              className="px-5 py-3.5 bg-gradient-to-r from-amber-600 via-rose-600 to-amber-700 hover:from-amber-500 hover:to-rose-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-amber-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isFr ? '🇩🇪 Ouvrir le Centre Allemagne (8)' : '🇩🇪 Zentrum Deutschland öffnen (8)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Cross-Border Law & Jurisdictions Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-900 border-2 border-indigo-500/60 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-indigo-500/30 text-indigo-300 border border-indigo-400/40 rounded flex items-center gap-1">
                  <span>🌐</span>
                  <span>ROME I • CISG • REVERSE CHARGE • VIES • M&A</span>
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  {isUk ? '9 Модулів' : isPl ? '9 Modułów' : isDe ? '9 Module' : isFr ? '9 Modules' : '9 Modules'}
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-indigo-600/30 text-indigo-200 border border-indigo-400/30 rounded">
                  €399/mo
                </span>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-indigo-200 flex items-center gap-2">
                  <span>🌐 {isUk ? 'ЦЕНТР ТРАНСКОРДОННОЇ ІНТЕГРАЦІЇ' : isPl ? 'CENTRUM INTEGRACJI TRANSGRANICZNEJ' : isDe ? 'GRENZÜBERSCHREITENDES ZENTRUM' : isFr ? 'CENTRE TRANSFRONTALIER' : 'CROSS-BORDER INTEGRATION HUB'}</span>
                </h3>
                <div className="text-[10px] font-bold text-indigo-400/90 uppercase tracking-wider mt-0.5">
                  {isUk ? 'Гармонізація Законодавства, Е-Фактур та Договорів' : isPl ? 'Harmonizacja Prawa, e-Faktur i Umów Międzynarodowych' : 'Cross-Border Law, Tax & Invoicing Interoperability'}
                </div>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isUk
                  ? 'Працюйте у своїй національній базі з безшовною адаптацією до іноземного партнера: гармонізація контрактів (Рим I / CISG), транскордонний ПДВ (Reverse Charge), крос-конвертація е-фактур (KSeF ↔ XRechnung ↔ Factur-X ↔ PEPPOL) та перевірка реєстрів (VIES, KRS, Handelsregister, INSEE, ЄДР).'
                  : isPl
                  ? 'Prowadź działalność w oparciu o krajowe przepisy z możliwością natychmiastowej adaptacji umów (Rzym I / CISG), rozliczania podatku VAT (Reverse Charge), konwersji e-faktur (KSeF ↔ XRechnung ↔ Factur-X) oraz weryfikacji w bazach VIES, KRS, Handelsregister i INSEE.'
                  : 'Operate locally while bridging cross-border transactions: contract harmonisation (Rome I / CISG), Reverse Charge VAT automation, e-invoice cross-conversion (KSeF ↔ XRechnung ↔ Factur-X ↔ PEPPOL), and registry KYC (VIES, KRS, Handelsregister, INSEE).'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('cross-border-center-hub', 'uk')}
              className="px-5 py-3.5 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isUk ? '🌐 Відкрити Центр Інтеграції (9)' : isPl ? '🌐 Otwórz Centrum Integracji (9)' : '🌐 Open Cross-Border Hub (9)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>


          {/* Admin Special Agents Hub Banner */}
          {isAdmin && (
            <>
              <div className="relative bg-gradient-to-r from-slate-950 via-purple-950 to-indigo-950 border-2 border-purple-500/60 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
                <div className="space-y-2.5 z-10">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/30 text-purple-300 border border-purple-400/40 rounded flex items-center gap-1">
                      <span>🛰️</span>
                      <span>ДЗЗ • СКАКО • ГНСС • СПЕЦКОНТРОЛЬ</span>
                    </span>
                    <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                      🎓 10 {isPl ? 'Modułów PhD' : isUk ? 'Модулів PhD' : 'PhD Modules'}
                    </span>
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/30 text-red-300 border border-red-400/30 rounded flex items-center gap-1">
                      <Crown className="w-3 h-3 text-red-400" />
                      ADMIN ONLY
                    </span>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-purple-200 flex items-center gap-2">
                      <span>🎓 {isPl ? 'CENTRUM DOKTORA FILOZOFII (PHD)' : isUk ? 'ЦЕНТР ДОКТОРА ФІЛОСОФІЇ (PHD)' : 'PHD DISSERTATION CENTER'}</span>
                    </h3>
                    <div className="text-[10px] font-bold text-purple-300/90 uppercase tracking-wider mt-0.5">
                      «Космічна підтримка екологічного сектору України»
                    </div>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed font-sans">
                    {isPl 
                      ? 'Dedykowane centrum syntezy rozprawy doktorskiej PhD: analiza teledetekcji (EO/SAR), świadomość orbitalna SKAKO/SSA, precyzyjne sieci GNSS, geofizyka GCKS, modele Data Fusion oraz generowanie pełnych rozdziałów 1-4 za normami NAQA/MEiN.'
                      : isUk 
                      ? 'Спеціалізований центр повного циклу підготовки дисертації доктора філософії: супутникове ДЗЗ (оптика/SAR), орбітальна безпека СКАКО, перманентні мережі ГНСС, геофізичний спецконтроль ГЦСК, моделі Data Fusion та генерація розділів 1–4 за нормами НАЗЯВО/МОН.'
                      : 'Dedicated full-cycle PhD dissertation research center: Earth Observation (optical/SAR), SSA/SKAKO orbital safety, GNSS geodesy networks, GCKS geophysics, Data Fusion models, and complete Chapters 1-4 generation under doctoral standards.'}
                  </p>
                </div>
                <button
                  onClick={() => onLaunchAgent('phd-center-hub')}
                  className="px-5 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
                >
                  <span>{isPl ? '🎓 Otwórz Centrum PhD (10)' : isUk ? '🎓 Відкрити Центр Дисертацій PhD (10)' : '🎓 Open PhD Center (10)'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              <div className="relative bg-gradient-to-r from-slate-950 via-amber-950 to-slate-900 border border-amber-500/50 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
                <div className="space-y-2.5 z-10">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/30 text-amber-300 border border-amber-400/40 rounded">
                      {isDe ? 'PROFILING • LÜGENDETEKTION • TRANSKRIPTION' : isFr ? 'PROFILAGE • DÉTECTEUR DE MENSONGE • TRANSCRIPTION' : isPl ? 'PROFILOWANIE • DETEKCJA KŁAMSTWA • TRANSKRYPCJA' : isUk ? 'ПРОФАЙЛІНГ • ДЕТЕКЦІЯ БРЕХНІ • ТРАНСКРИБАЦІЯ' : 'PROFILING • LIE DETECTION • AUDIO'}
                    </span>
                    <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/30 text-red-300 border border-red-400/30 rounded flex items-center gap-1">
                      👑 4 {isDe ? 'Admin-Module' : isFr ? 'Modules Admin' : isPl ? 'Moduły Admina' : isUk ? 'Адмін-Агенти' : 'Admin Agents'}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-amber-200 flex items-center gap-2">
                    <span>👑 {isDe ? 'ZENTRUM DER ADMIN-AGENTEN' : isFr ? 'CENTRE DES AGENTS ADMIN' : isPl ? 'CENTRUM AGENTÓW ADMINA' : isUk ? 'ЦЕНТР АДМІН-АГЕНТІВ' : 'ADMIN AGENT CENTER'}</span>
                  </h3>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    {isDe
                      ? 'Exklusives Zentrum für Administratoren: nonverbales Profiling, Lügenerkennung, Besprechungstranskription, Lead-Harvesting und Bräuche.'
                      : isFr
                      ? 'Centre exclusif pour les administrateurs : profilage non-verbal, détection de mensonge, transcription de réunions, collecte de leads et traditions.'
                      : isPl
                      ? 'Ekskluzywne centrum administratora: niewerbalne profilowanie, detekcja kłamstwa, transkrypcja spotkań, pozyskiwanie kontaktów i tradycje.'
                      : isUk 
                      ? 'Ексклюзивний центр для адміністраторів: невербальний профайлінг & детекція брехні, транскрибація переговорів, парсинг контактів та цілительство.'
                      : 'Exclusive center for administrators: nonverbal profiling & lie detection, meeting transcription, lead harvesting & folk traditions.'}
                  </p>
                </div>
                <button
                  onClick={() => onLaunchAgent('admin-center-hub')}
                  className="px-5 py-3.5 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-slate-950 font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-amber-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
                >
                  <span>{isDe ? '👑 Admin-Zentrum öffnen (4)' : isFr ? '👑 Ouvrir le Centre Admin (4)' : isPl ? '👑 Otwórz Centrum Admina (4)' : isUk ? '👑 Відкрити Адмін-Центр (4)' : '👑 Open Admin Center (4)'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {/* Office Applications Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  EXCEL • CSV • SUMIF • NORMY
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
                  🏢 6 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агентів' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🏢 {isDe ? 'ZENTRUM FÜR BÜROANWENDUNGEN' : isFr ? 'CENTRE D\'APPLICATIONS BUREAUTIQUES' : isPl ? 'CENTRUM APLIKACJI BIUROWYCH' : isUk ? 'ЦЕНТР ОФІСНИХ ДОДАТКІВ' : 'OFFICE APPLICATIONS HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Analyse von Excel- & CSV-Tabellen, bedingte Summierung (SUMIF), normgerechte Dokumentenerstellung, Übersetzung und Lektorat.'
                  : isFr
                  ? 'Analyse de tableaux Excel & CSV, sommes conditionnelles (SUMIF), rédaction de documents officiels, traduction et relecture.'
                  : isPl
                  ? 'Analiza tabel Excel & CSV, warunkowe sumowanie (SUMIF), redagowanie dokumentów urzędowych, tłumaczenie i korekta językowa.'
                  : isUk 
                  ? 'Аналіз таблиць Excel & CSV, умовне підсумовування (SUMIF), діловодство за ДСТУ 4163:2020, переклад та коректура.'
                  : 'Excel & CSV table analysis, SUMIF conditional summing, DSTU 4163 document drafting & auditing, proofreading & translation.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('office-center-hub')}
              className="px-5 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🏢 Büro-Zentrum öffnen (6)' : isFr ? '🏢 Ouvrir le Centre Bureautique (6)' : isPl ? '🏢 Otwórz Centrum Biurowe (6)' : isUk ? '🏢 Відкрити Офісний Центр (6)' : '🏢 Open Office Hub (6)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Business Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 border border-blue-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
                  {isDe ? 'BUSINESS • MÄRKTE • RECHT • MARKEN' : isFr ? 'AFFAIRES • MARCHÉS • DROIT • MARQUES' : isPl ? 'BIZNES • RYNKI • PRAWO • ZNAKI TOWAROWE' : isUk ? 'БІЗНЕС • РИНКИ • ЮРИСПРУДЕНЦІЯ • ТМ' : 'BUSINESS • MARKETS • LEGAL • TM'}
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/30 text-purple-300 border border-purple-400/40 rounded">
                  💼 25 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агентів' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>💼 {isDe ? 'WIRTSCHAFT & JURISPRUDENZ' : isFr ? 'AFFAIRES & JURISPRUDENCE' : isPl ? 'BIZNES & JURYSPRUDENCJA' : isUk ? 'БІЗНЕС & ЮРИСПРУДЕНЦІЯ' : 'BUSINESS & LEGAL HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Vertragsprüfung, Marktanalyse, Vertriebsleitfäden, Kreditrisikobewertung, Markenschutz, Ausschreibungen, HR und DSGVO.'
                  : isFr
                  ? 'Audit de contrats, analyse de marché, argumentaires de vente, risque de crédit, marques, appels d\'offres, RH et RGPD.'
                  : isPl
                  ? 'Audyt umów, analiza rynków, skrypty sprzedażowe, ocena ryzyka kredytowego, ochrona znaków towarowych, przetargi, rekrutacja i RODO/BCP.'
                  : isUk 
                  ? 'Аудит договорів, аналіз ринків, скрипти продажів, кредитування, ТМ, Prozorro, найм, BCP та GDPR.'
                  : 'Contract audits, market intelligence, sales playbooks, credit risk, trademark, Prozorro, HR & GDPR.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('business-center-hub')}
              className="px-5 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🚀 Business-Hub öffnen (25)' : isFr ? '🚀 Ouvrir le Hub Affaires (25)' : isPl ? '🚀 Otwórz Centrum Biznesu (25)' : isUk ? '🚀 Відкрити Бізнес-Центр (25)' : '🚀 Open Business Hub (25)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Tax Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  {isDe ? 'STEUERN • XRECHNUNG • KSt / USt / ESt' : isFr ? 'FISCALITÉ • FACTUR-X • IS / TVA / IRPF' : isPl ? 'PODATKI • KSeF • PIT / CIT / VAT' : isUk ? 'ПКУ • ЗВІТНІСТЬ ДПС • ДЕКЛАРАЦІЇ' : 'TAX CODE • FILINGS • DECLARATIONS'}
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
                  🏛️ 19 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агентів' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🏛️ {isDe ? 'STEUERZENTRUM & ABRECHNUNGEN' : isFr ? 'CENTRE FISCAL & DÉCLARATIONS' : isPl ? 'CENTRUM PODATKOWE & ROZLICZENIA' : isUk ? 'ПОДАТКОВИЙ ЦЕНТР & ДПС' : 'TAX COMMAND CENTER'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Steuerberechnung, Erstellung von Steuererklärungen (Gewerbesteuer, Körperschaftsteuer, Umsatzsteuer, Lohnsteuer, Verbrauchsteuern).'
                  : isFr
                  ? 'Calcul des impôts et taxes, préparation des déclarations (IS, TVA, micro-entreprise, salaires et charges sociales, accises).'
                  : isPl
                  ? 'Kalkulacja podatków i opłat, sporządzanie deklaracji (JDG, Ryczałt, CIT, VAT, Płace i ZUS, Akcyza).'
                  : isUk 
                  ? 'Розрахунок податків і зборів ПКУ, складання декларацій (ФОП, ЄП 4, Прибуток, ПДВ, Зарплата, Акциз, Дія Сіті).'
                  : 'Tax calculation & filing hub for all Tax Code duties with automated compliance checks.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('tax-center-hub')}
              className="px-5 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🏛️ Steuerzentrum öffnen (19)' : isFr ? '🏛️ Ouvrir le Centre Fiscal (19)' : isPl ? '🏛️ Otwórz Centrum Podatkowe (19)' : isUk ? '🏛️ Відкрити Податковий Центр (19)' : '🏛️ Open Tax Center (19)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Energy Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-amber-950 border border-amber-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/30 text-amber-300 border border-amber-400/40 rounded">
                  {isDe ? 'ENERGIEAUDIT • ISO 50001 • PV & BESS' : isFr ? 'AUDIT ÉNERGÉTIQUE • ISO 50001 • PV & BESS' : isPl ? 'AUDYT ENERGETYCZNY • ISO 50001 • PV & BESS' : isUk ? 'ЕНЕРГОАУДИТ • ISO 50001 • СЕС' : 'ENERGY AUDIT • ISO 50001 • SOLAR'}
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  ⚡ 5 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агентів' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>⚡ {isDe ? 'ENERGIEAUDIT-ZENTRUM' : isFr ? 'CENTRE D\'AUDIT ÉNERGÉTIQUE' : isPl ? 'CENTRUM AUDYTU ENERGETYCZNEGO' : isUk ? 'ЦЕНТР ЕНЕРГОАУДИТУ' : 'ENERGY AUDIT HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Energieausweise für Gebäude, ISO 50001, Wirtschaftlichkeitsberechnung für PV & BESS, ESCO-Verträge und Smart Metering.'
                  : isFr
                  ? 'Diagnostics de performance énergétique, ISO 50001, calcul de rentabilité PV & BESS, contrats de performance ESCO et compteurs communicants.'
                  : isPl
                  ? 'Świadectwa charakterystyki energetycznej budynków, ISO 50001, kalkulacja opłacalności PV/BESS, kontrakty ESCO i systemy AMI.'
                  : isUk 
                  ? 'Енергетичні паспорти ДБН, ISO 50001, розрахунок окупності СЕС/BESS, ЕСКО перформанс-контракти та АСКОЕ.'
                  : 'Building energy passports, ISO 50001 systems, solar BESS sizing, ESCO contracts & AMI analytics.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('energy-center-hub')}
              className="px-5 py-3.5 bg-amber-600 hover:bg-amber-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-amber-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '⚡ Energie-Zentrum öffnen (5)' : isFr ? '⚡ Ouvrir le Centre Énergie (5)' : isPl ? '⚡ Otwórz Centrum Energii (5)' : isUk ? '⚡ Відкрити Енерго-Центр (5)' : '⚡ Open Energy Hub (5)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Space Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-900 border border-indigo-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
                  {isDe ? 'RAUMFAHRT-OPERATIONEN • FERNERKUNDUNG • SATELLITEN' : isFr ? 'OPÉRATIONS SPATIALES • TÉLÉDÉTECTION • SATELLITES' : isPl ? 'OPERACJE KOSMICZNE • SPECKONTROLA • SATELITY' : isUk ? 'КОСМІЧНІ ОПЕРАЦІЇ • СПЕЦКОНТРОЛЬ • ДЗЗ' : 'SPACE OPERATIONS • SATELLITES'}
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/30 text-purple-300 border border-purple-400/40 rounded">
                  ✨ 30 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агентів' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🛰️ {isDe ? 'RAUMFAHRT, NAVIGATION & FERNERKUNDUNG' : isFr ? 'ESPACE, NAVIGATION & TÉLÉDÉTECTION' : isPl ? 'KOSMOS, NAWIGACJA & SATELITY' : isUk ? 'КОСМОС, НАВІГАЦІЯ & ДЗЗ' : 'SPACE & SATELLITE HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Verarbeitung und Download von Satellitenbildern, Überwachung des Weltraums, seismische CTBTO-Kontrolle und GNSS-Resilienz.'
                  : isFr
                  ? 'Traitement et téléchargement d\'images satellitaires, surveillance spatiale, contrôle sismique CTBTO et résilience GNSS.'
                  : isPl
                  ? 'Przetwarzanie i pobieranie zdjęć satelitarnych, monitoring przestrzeni kosmicznej, kontrola sejsmiczna CTBTO i odporność GNSS.'
                  : isUk 
                  ? 'Обробка та викачування супутникових знімків ДЗЗ, моніторинг космічного простору, CTBTO сейсмоконтроль.'
                  : 'Satellite imagery harvesting, space domain awareness, CTBTO seismic monitoring & GNSS resilience.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('space-center-hub')}
              className="px-5 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🚀 Raumfahrt-Hub öffnen (30)' : isFr ? '🚀 Ouvrir le Hub Spatial (30)' : isPl ? '🚀 Otwórz Centrum Kosmosu (30)' : isUk ? '🚀 Відкрити Космічний Центр (30)' : '🚀 Open Space Hub (30)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Agri Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  ARiMR • NPK • EGiB • NDVI
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/30 text-amber-300 border border-amber-400/40 rounded">
                  🌾 4 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агенти' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🌾 {isDe ? 'AGRI-TECH & PRÄZISIONSLANDWIRTSCHAFT' : isFr ? 'CENTRE AGRI-TECH & AGRICULTURE DE PRÉCISION' : isPl ? 'CENTRUM AGRO & ROLNICTWA PRECYZYJNEGO' : isUk ? 'АГРО-ЦЕНТР & ЗЕМЕЛЬНИЙ КОМПЛАЄНС' : 'AGRI-TECH COMMAND CENTER'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Auswahl von Agrarförderungen, NPK-Bodenanalysen, Pachtvertragsprüfung und satellitengestützte NDVI-Ertragsüberwachung.'
                  : isFr
                  ? 'Attribution des aides agricoles, analyse des sols NPK, audit des baux ruraux et suivi satellitaire des cultures NDVI.'
                  : isPl
                  ? 'Dobór dotacji rolniczych, analiza glebowa NPK, audyt dzierżawy gruntów oraz satelitarny monitoring wegetacji NDVI.'
                  : isUk 
                  ? 'Підбір субсидій у ДАР, аналіз ґрунтових проб NPK, аудит оренди земельних паїв та супутниковий моніторинг врожайності.'
                  : 'DAR subsidy matching, NPK soil lab analytics, farmland lease audit & satellite yield forecasting.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('agri-center-hub')}
              className="px-5 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🌾 Agri-Hub öffnen (4)' : isFr ? '🌾 Ouvrir le Hub Agricole (4)' : isPl ? '🌾 Otwórz Centrum Agro (4)' : isUk ? '🌾 Відкрити Агро-Центр (4)' : '🌾 Open Agri Hub (4)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Logistics Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 border border-blue-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/30 text-blue-300 border border-blue-400/40 rounded">
                  TARIC • SAD • e-KOLEJKA • TP/CFC
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-indigo-500/30 text-indigo-300 border border-indigo-400/40 rounded">
                  🚛 3 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агенти' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🚛 {isDe ? 'LOGISTIK, ZOLL & AUSSENHANDEL' : isFr ? 'LOGISTIQUE, DOUANES & COMMERCE EXTÉRIEUR' : isPl ? 'LOGISTYKA, CŁA & HANDEL ZAGRANICZNY' : isUk ? 'ЛОГІСТИКА, МИТНИЦЯ & ЗЕД' : 'LOGISTICS & CUSTOMS HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Zolltarif-Klassifizierung, Berechnung von Einfuhrabgaben und MwSt., Grenzlogistik und Verrechnungspreis-Audits.'
                  : isFr
                  ? 'Classification tarifaire douanière, calcul des droits et TVA à l\'importation, optimisation des flux frontaliers et audit des prix de transfert.'
                  : isPl
                  ? 'Kodowanie taryf celnych, kalkulacja cła i VAT w imporcie, logistyka kolejek granicznych oraz audyt cen transferowych (TP/CFC).'
                  : isUk 
                  ? 'Кодування УКТ ЗЕД, розрахунок ввізного мита та ПДВ, оптимізація маршрутів єЧерги та аудит ТЦО/КІК.'
                  : 'HS Code UKT ZED matching, import duty & VAT calculation, eQueue logistics & Transfer Pricing/CFC audit.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('logistics-center-hub')}
              className="px-5 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🚛 Logistik-Hub öffnen (3)' : isFr ? '🚛 Ouvrir le Hub Logistique (3)' : isPl ? '🚛 Otwórz Logistykę (3)' : isUk ? '🚛 Відкрити Логістику (3)' : '🚛 Open Logistics Hub (3)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Cyber Security Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-red-950 to-slate-900 border border-red-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/30 text-red-300 border border-red-400/40 rounded">
                  ISO 27001 • NIS2 • SOC • CSIRT
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-violet-500/30 text-violet-300 border border-violet-400/40 rounded">
                  🔒 3 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агенти' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🔒 {isDe ? 'CYBERSICHERHEIT & IT-COMPLIANCE' : isFr ? 'CYBERSÉCURITÉ & CONFORMITÉ IT' : isPl ? 'CYBERBEZPIECZEŃSTWO & IT-COMPLIANCE' : isUk ? 'КІБЕРБЕЗПЕКА & IT-КОМПЛАЄНС' : 'CYBERSECURITY & IT HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Sicherheitsaudits kritischer Infrastrukturen, ISO 27001- / NIS2-Zertifizierung und Untersuchung von Sicherheitsvorfällen.'
                  : isFr
                  ? 'Audit de sécurité des infrastructures critiques, certification ISO 27001 / NIS2 et gestion des incidents de sécurité SIEM SOC.'
                  : isPl
                  ? 'Audyt bezpieczeństwa infrastruktury krytycznej, certyfikacja ISO 27001 / NIS2 oraz obsługa incydentów bezpieczeństwa.'
                  : isUk 
                  ? 'Аудит КСЗІ для об\'єктів критичної інфраструктури (КІІ), сертифікація ISO 27001/NIS2 та розслідування кіберінцидентів.'
                  : 'KSZI critical infrastructure audit, ISO 27001 / NIS2 compliance & SIEM SOC incident response.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('cyber-center-hub')}
              className="px-5 py-3.5 bg-red-600 hover:bg-red-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-red-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🔒 Cyber-Hub öffnen (3)' : isFr ? '🔒 Ouvrir le Hub Cyber (3)' : isPl ? '🔒 Otwórz Cyber-Centrum (3)' : isUk ? '🔒 Відкрити Кібер-Центр (3)' : '🔒 Open Cyber Hub (3)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Construction Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-sky-950 to-slate-900 border border-sky-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-sky-500/30 text-sky-300 border border-sky-400/40 rounded">
                  KOSZTORYSY • SEKO • PROJEKTY • FIDIC
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-orange-500/30 text-orange-300 border border-orange-400/40 rounded">
                  🏗️ 4 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агенти' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🏗️ {isDe ? 'BAUZENTRUM & KOSTENSCHÄTZUNG' : isFr ? 'CENTRE BTP, DEVIS & RECONSTRUCTION' : isPl ? 'CENTRUM BUDOWNICTWA & KOSZTORYSÓW' : isUk ? 'БУДІВНИЦТВО, ДІАМ & ВІДБУДОВА' : 'CONSTRUCTION & DIAM HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Prüfung von Planungs- und Kostendokumenten, Baukostenkalkulation nach Normen, Schadensbewertung und Bebauungspläne.'
                  : isFr
                  ? 'Audit des pièces de marchés de travaux et CCTP, devis et métrés conformes, estimation des dommages et plans d\'urbanisme.'
                  : isPl
                  ? 'Audyt dokumentacji projektowo-kosztorysowej, kalkulacja kosztorysów wg norm, wycena szkód i plany zagospodarowania terenu.'
                  : isUk 
                  ? 'Аудит проєктно-кошторисних документів ЄДЕССБ/ДІАМ, розрахунок кошторисів за ДБН/РЕКН, оцінка пошкоджень майна (ПКМУ № 423) та розробка ДПТ.'
                  : 'DIAM / EDESSB permit documentation audit, DBN cost estimates, war damage valuation per CMU 423 & detailed territory plans.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('construction-center-hub')}
              className="px-5 py-3.5 bg-sky-600 hover:bg-sky-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-sky-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🏗️ Bau-Hub öffnen (4)' : isFr ? '🏗️ Ouvrir le Hub BTP (4)' : isPl ? '🏗️ Otwórz Budownictwo (4)' : isUk ? '🏗️ Відкрити Будівництво (4)' : '🏗️ Open Construction Hub (4)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Science & Innovation Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-purple-950 to-slate-900 border border-purple-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/30 text-purple-300 border border-purple-400/40 rounded">
                  DEEP RESEARCH • CANVAS • PODCAST • R&D
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-indigo-500/30 text-indigo-300 border border-indigo-400/40 rounded">
                  🎓 19 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Modułów' : isUk ? 'Модулів' : 'Modules'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🎓 {isDe ? 'WISSENSCHAFT, F&E & INNOVATION' : isFr ? 'CENTRE SCIENCES, R&D & INNOVATION' : isPl ? 'CENTRUM NAUKI, B+R & INNOWACJI' : isUk ? 'НАУКОВИЙ ЦЕНТР, R&D & ІННОВАЦІЇ' : 'SCIENCE, R&D & INNOVATION HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Innovationszentrum 2026 (Canvas, Podcasts, MCP, Change Detection), flagowy silnik Deep Research, Horizon Europe-Förderungen, Scopus-Reviews und Patente.'
                  : isFr
                  ? 'Hub d\'Innovation 2026 (Éditeur Canvas, Podcasts IA, connecteurs MCP, Change Detection), moteur Deep Research, subventions Horizon Europe, revues Scopus et brevets.'
                  : isPl
                  ? 'Centrum Innowacji 2026 (Canvas, Podcasty, MCP, Change Detection), flagowy silnik Deep Research, granty Horizon Europe, recenzje Scopus i patenty.'
                  : isUk 
                  ? 'Центр Інновацій 2026 (Canvas-редактор, ШІ-подкасти, MCP-конектори, Change Detection), флагманський Deep Research, гранти Horizon, Scopus та патенти.'
                  : '2026 Innovation Hub (Canvas editor, AI Podcasts, MCP connectors, Change Detection), Deep Research engine, Horizon Europe grants & Scopus reviews.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('science-center-hub')}
              className="px-5 py-3.5 bg-purple-600 hover:bg-purple-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🎓 Wissenschafts-Hub öffnen (19)' : isFr ? '🎓 Ouvrir le Hub Sciences & Innovation (19)' : isPl ? '🎓 Otwórz Centrum Nauki & Innowacji (19)' : isUk ? '🎓 Відкрити Центр Науки & Інновацій (19)' : '🎓 Open Science & Innovation Hub (19)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Pharma & Healthcare Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-teal-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  URPL • GIF • ChPL • ATC • GDP/GSP
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-cyan-500/30 text-cyan-300 border border-cyan-400/40 rounded">
                  🏥 7 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Агентів' : 'Agents'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🏥 {isDe ? 'PHARMAZEUTISCHES & MEDIZINISCHES ZENTRUM' : isFr ? 'CENTRE PHARMACEUTIQUE & MÉDICAL' : isPl ? 'CENTRUM FARMACEUTYCZNO-MEDYCZNE' : isUk ? 'ФАРМАЦЕВТИЧНИЙ & МЕДИЧНИЙ ЦЕНТР' : 'PHARMA & HEALTHCARE HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Prüfung der Arzneimittelkompatibilität nach offiziellen Registern, Wechselwirkungen, Qualitätszertifikate, Apothekenzulassungen und Erstattungsprüfung.'
                  : isFr
                  ? 'Vérification de compatibilité des médicaments selon les registres officiels, interactions médicamenteuses, certificats de qualité, licences d\'officine et remboursements.'
                  : isPl
                  ? 'Analiza kompatybilności leków wg rejestrów, interakcje lekowe, certyfikaty jakości, licencjonowanie aptek i audyt refundacji.'
                  : isUk 
                  ? 'Аналіз сумісності ліків за довідником ДЛУ, міжлікарські взаємодії, сертифікати якості ДЛС, ліцензування аптек та реімбурсація НСЗУ.'
                  : 'Drug compatibility & interaction checker via DLU register, DLS certificates, pharmacy licensing & NHSU reimbursement.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('pharma-center-hub')}
              className="px-5 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🏥 Pharma-Hub öffnen (7)' : isFr ? '🏥 Ouvrir le Hub Pharma (7)' : isPl ? '🏥 Otwórz Centrum Farmacji (7)' : isUk ? '🏥 Відкрити Фарм-Центр (7)' : '🏥 Open Pharma Hub (7)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Eco, Climate & Green Deal Hub Banner */}
          <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 text-white shadow-xl overflow-hidden flex flex-col justify-between gap-5 font-mono">
            <div className="space-y-2.5 z-10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
                  EU GREEN DEAL • EUDR 2023/1115 • EU LIFE • RD4U
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-teal-500/30 text-teal-300 border border-teal-400/40 rounded">
                  🌿 7 {isDe ? 'Module' : isFr ? 'Modules' : isPl ? 'Agentów' : isUk ? 'Модулів' : 'Modules'}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-extrabold uppercase tracking-wide text-white flex items-center gap-2">
                <span>🌿 {isDe ? 'ZENTRUM FÜR ÖKOLOGIE, KLIMA & GREEN DEAL' : isFr ? 'CENTRE ÉCOLOGIE, CLIMAT & GREEN DEAL' : isPl ? 'CENTRUM EKOLOGII, KLIMATU & GREEN DEAL' : isUk ? 'ЕКОЛОГІЧНИЙ ЦЕНТР, КЛІМАТ & GREEN DEAL' : 'ECO, CLIMATE & GREEN DEAL HUB'}</span>
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                {isDe
                  ? 'Satellitengestützter Umweltschutz: EUDR-Entwaldungsprüfung, Flussgebietsüberwachung, Methanemissionen auf Deponien, EU-LIFE-Förderanträge und Kriegsschadensdokumentation.'
                  : isFr
                  ? 'Protection de l\'environnement par satellite : conformité déforestation EUDR, qualité des bassins versants, fuites de méthane, subventions EU LIFE et écocide.'
                  : isPl
                  ? 'Satelitarny monitoring środowiska: weryfikacja wylesiania EUDR 2023/1115, jakość wód w dorzeczach, metan na wysypiskach, dotacje EU LIFE i wycena strat wojennych RD4U.'
                  : isUk 
                  ? 'Космічна підтримка еко-сектору: розробка регіональних програм для ОВА, гранти EU LIFE / Horizon, аудит вирубок EUDR 2023/1115, якість річкових басейнів, полігони ТПВ та фіксація екоциду для Реєстру в Гаазі (RD4U).'
                  : 'Satellite environmental intelligence: regional eco-programs, EU LIFE & Horizon grants, EUDR 2023/1115 deforestation audit, river basin quality, landfill safety & war ecocide.'}
              </p>
            </div>
            <button
              onClick={() => onLaunchAgent('eco-center-hub')}
              className="px-5 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 cursor-pointer z-10 hover:scale-[1.02]"
            >
              <span>{isDe ? '🌿 Öko-Hub öffnen (7)' : isFr ? '🌿 Ouvrir le Hub Écologie (7)' : isPl ? '🌿 Otwórz Centrum Ekologii (7)' : isUk ? '🌿 Відкрити Еко-Центр (7)' : '🌿 Open Eco & Green Deal Hub (7)'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        )}
      </div>

      {/* 3. BOTTOM SECTION: COLLAPSIBLE FULL AGENTS CATALOG */}
      <div className="space-y-4 pt-4 font-mono">
        <div className="flex items-center justify-between p-4 sm:p-5 bg-gradient-to-r from-slate-900 via-slate-950 to-slate-900 border-2 border-slate-700/60 rounded-2xl shadow-xl flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-slate-800 border border-slate-600 rounded-xl text-emerald-400 shrink-0">
              <Cpu className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm sm:text-base font-extrabold uppercase text-white tracking-wide">
                  {isDe 
                    ? '⚡ VOLLSTÄNDIGER KATALOG DER FERTIGEN KI-WERKZEUGE' 
                    : isFr 
                    ? '⚡ CATALOGUE COMPLET DES OUTILS ET MODULES IA' 
                    : isPl 
                    ? '⚡ PEŁNY KATALOG GOTOWYCH NARZĘDZI I MODUŁÓW AI' 
                    : isUk 
                    ? '⚡ ПОВНИЙ КАТАЛОГ ГОТОВИХ ШІ-ІНСТРУМЕНТІВ ТА МОДУЛІВ' 
                    : '⚡ FULL CATALOG OF AI TOOLS & MODULES'}
                </h2>
                <span className="px-2 py-0.5 text-[9px] bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded font-bold uppercase">
                  326+ Модулів
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {isUk ? 'Індивідуальні картки готових модулів з тарифами та прямим запуском' : 'Browse individual module cards with pricing and direct launch'}
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsCatalogExpanded(!isCatalogExpanded)}
            className="px-5 py-2.5 bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-600 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer border border-slate-600 hover:scale-[1.02]"
          >
            <span>{isCatalogExpanded ? (isUk ? '📁 Згорнути Каталог' : isPl ? '📁 Zwiń Katalog' : '📁 Collapse Catalog') : (isUk ? '📂 Показати всі 326+ Карток' : isPl ? '📂 Pokaż 326+ Kart' : '📂 Show 326+ Cards')}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isCatalogExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Collapsible Agents Grid */}
        {isCatalogExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
          {standaloneAgents.map((agent: AgentItem) => {
            const colors = getColorClasses(agent.color);
            const agentPricing = pricing[agent.id];
            const hasPricing = !!agentPricing;
            const priceVal = agentPricing ? agentPricing.price : null;
            const limitVal = agentPricing ? agentPricing.limit : null;
            const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;

            const isEnterpriseFree = isEnterpriseAdmin || (hasEnterprise && allowedEnterpriseAgents.includes(agent.id));
            const isEnterpriseRestrictedPaid = hasEnterprise && !isEnterpriseAdmin && !allowedEnterpriseAgents.includes(agent.id);

            const userSub = mySubscriptions.find(s => s.agent_id === agent.id);

            return (
              <div 
                key={agent.id}
                className={`group relative bg-white border border-slate-200 hover:shadow-md rounded-2xl p-6 flex flex-col justify-between transition-all duration-300 ${colors.hoverBorder}`}
              >
                <div className={`absolute top-0 right-0 w-24 h-24 rounded-bl-full pointer-events-none transition-colors ${colors.glowBg}`} />
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl border ${colors.iconBg}`}>
                      <AgentIcon name={agent.icon} className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[8px] font-bold uppercase border px-1.5 py-0.5 rounded bg-slate-100 border-slate-200 text-slate-500">
                          {getLocalizedAgentCategory(agent, language)}
                        </span>
                        <span className={`text-[8px] font-bold uppercase border px-1.5 py-0.5 rounded ${
                          agent.model === 'gemini-3.7-flash'
                            ? 'bg-purple-50 text-purple-700 border-purple-300 font-extrabold'
                            : 'bg-slate-50 text-slate-600 border-slate-200'
                        }`}>
                          {agent.model === 'gemini-3.7-flash' ? '✨ Gemini 3.7 Flash' : '⚡ Gemini 3.7 Flash'}
                        </span>
                        {isEnterpriseFree ? (
                          <span className="text-[8px] font-black uppercase border px-2 py-0.5 rounded bg-emerald-100 text-emerald-850 border-emerald-300 flex items-center gap-1 shadow-xs">
                            <span>🏛️</span>
                            <span>{isUk ? `Безкоштовно (${enterpriseName || 'НЦУВКЗ'})` : isPl ? `Bezpłatnie (${enterpriseName || 'Firma'})` : `Free (${enterpriseName || 'Enterprise'})`}</span>
                          </span>
                        ) : (
                          <>
                            {hasPricing && (
                              <span className={`text-[8px] font-bold uppercase border px-1.5 py-0.5 rounded ${
                                priceVal === 0 
                                  ? 'bg-emerald-50 text-emerald-600 border-emerald-200' 
                                  : 'bg-red-50 text-red-500 border-red-200'
                              }`}>
                                {priceVal === 0 
                                  ? (isDe ? 'Kostenlos' : isFr ? 'Gratuit' : isPl ? 'Bezpłatnie' : isUk ? 'Безкоштовно' : 'Free')
                                  : `${formatPriceWithCurrency(priceVal, currency)} / ${limitVal} ${isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : isDe ? 'Anfragen' : isFr ? 'demandes' : 'req.'} (${durationDaysVal} ${isPl ? 'dni' : isUk ? 'дн.' : isDe ? 'Tage' : isFr ? 'jours' : 'days'})`}
                              </span>
                            )}
                            {hasPricing && priceVal !== 0 && (agentPricing as any).singlePrice > 0 && (
                              <span className="text-[8px] font-bold uppercase border px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-650 border-emerald-200 font-semibold">
                                {isDe ? `Einzeln: ${formatPriceWithCurrency((agentPricing as any).singlePrice, currency)}` : isFr ? `À l'unité : ${formatPriceWithCurrency((agentPricing as any).singlePrice, currency)}` : isPl ? `Pojedynczo: ${formatPriceWithCurrency((agentPricing as any).singlePrice, currency)}` : isUk ? `Разово: ${formatPriceWithCurrency((agentPricing as any).singlePrice, currency)}` : `Single: ${formatPriceWithCurrency((agentPricing as any).singlePrice, currency)}`}
                              </span>
                            )}
                            {isEnterpriseRestrictedPaid && (
                              <>
                                <span className="text-[8px] font-black uppercase border px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border-amber-300 flex items-center gap-1">
                                  <span>🔒</span>
                                  <span>{isUk ? 'Платний' : isPl ? 'Płatny' : 'Paid'}</span>
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onTogglePinAgent && onTogglePinAgent(agent.id);
                                  }}
                                  className={`text-[8px] font-black uppercase border px-1.5 py-0.5 rounded transition-all flex items-center gap-1 cursor-pointer ${
                                    pinnedEnterpriseAgents.includes(agent.id)
                                      ? "bg-amber-100 text-amber-900 border-amber-400 shadow-xs"
                                      : "bg-slate-50 hover:bg-amber-50 text-slate-600 hover:text-amber-800 border-slate-200 hover:border-amber-300"
                                  }`}
                                  title={pinnedEnterpriseAgents.includes(agent.id) ? (isUk ? `Закріплено в кабінеті ${enterpriseName || 'НЦУВКЗ'}` : 'Pinned to Enterprise') : (isUk ? `Закріпити в кабінеті ${enterpriseName || 'НЦУВКЗ'}` : 'Pin to Enterprise')}
                                >
                                  <span>📌</span>
                                  <span>{pinnedEnterpriseAgents.includes(agent.id) ? (isUk ? 'В кабінеті' : 'Pinned') : (isUk ? '+ Закріпити' : '+ Pin')}</span>
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wide mt-1.5">
                        {getLocalizedAgentName(agent, language)}
                      </h3>
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-600 leading-relaxed font-mono">
                    {getLocalizedAgentDesc(agent, language)}
                  </p>

                  <ul className="space-y-1.5 text-[10px] text-slate-600 border-t border-slate-100 pt-3">
                    {getLocalizedAgentFeatures(agent, language).map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${colors.bulletBg}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Subscription status display */}
                  <div className="mt-4 pt-3 border-t border-slate-100 text-[10px] space-y-1">
                    {(() => {
                      const isUnlimitedAgent = userProfile?.unlimited_all || (Array.isArray(userProfile?.unlimited_agents) && userProfile.unlimited_agents.includes(agent.id));
                      if (isAdmin) {
                        return (
                          <div className="flex items-center gap-1.5 text-emerald-600 font-bold bg-emerald-50 px-2.5 py-1.5 rounded border border-emerald-100">
                            <span>✓</span>
                            <span>{isDe ? 'Unbegrenzter Zugang (Administrator)' : isFr ? 'Accès illimité (Administrateur)' : isPl ? 'Dostęp nielimitowany (Administrator)' : isUk ? 'Безлімітний доступ (Адміністратор)' : 'Unlimited Access (Admin)'}</span>
                          </div>
                        );
                      }
                      if (isEnterpriseFree) {
                        return (
                          <div className="flex items-center gap-1.5 text-emerald-800 font-bold bg-emerald-50 px-2.5 py-1.5 rounded border border-emerald-300 shadow-xs">
                            <Sparkles className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
                            <span>{isUk ? `🏛️ Безкоштовний доступ від ${enterpriseName || 'НЦУВКЗ'}` : isPl ? `🏛️ Bezpłatny dostęp od ${enterpriseName || 'Firmy'}` : `🏛️ Free access provided by ${enterpriseName || 'Enterprise'}`}</span>
                          </div>
                        );
                      }
                      if (isEnterpriseRestrictedPaid) {
                        if (userSub) {
                          return (
                            <div className="bg-blue-50 border border-blue-100 rounded p-2.5 text-blue-700 space-y-1">
                              <div className="font-bold flex justify-between">
                                <span>{isDe ? 'AKTIVES ABONNEMENT' : isFr ? 'ABONNEMENT ACTIF' : isPl ? 'AKTYWNA SUBSKRYPCJA' : isUk ? 'АКТИВНА ПІДПИСКА' : 'ACTIVE SUBSCRIPTION'}</span>
                                <span className="text-blue-500">✓</span>
                              </div>
                              <div className="flex justify-between text-[9px] text-blue-600">
                                <span>{isDe ? 'Verbleibende Anfragen:' : isFr ? 'Demandes restantes :' : isPl ? 'Pozostało zapytań:' : isUk ? 'Залишилось запитів:' : 'Requests left:'}</span>
                                <span className="font-bold">
                                  {Math.max(0, userSub.requests_limit - userSub.requests_used)} / {userSub.requests_limit}
                                </span>
                              </div>
                              <div className="flex justify-between text-[9px] text-blue-600">
                                <span>{isDe ? 'Gültig bis:' : isFr ? 'Expire le :' : isPl ? 'Ważna do:' : isUk ? 'Діє до:' : 'Expires:'}</span>
                                <span className="font-bold">{formatDate(userSub.expires_at)}</span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="bg-amber-50/80 border border-amber-200 rounded p-2 text-amber-800 space-y-1">
                            <div className="flex items-center justify-between font-bold text-[10px]">
                              <span className="flex items-center gap-1 text-amber-800 font-extrabold">
                                <span>🔒</span>
                                <span>{isUk ? 'Платний доступ (Поза квотою підприємства)' : isPl ? 'Dostęp płatny (Poza pakietem)' : 'Paid Access (Outside enterprise pack)'}</span>
                              </span>
                            </div>
                            <p className="text-[9px] text-amber-700 font-medium leading-tight">
                              {isUk ? 'Оплата за окремим тарифом або оформлення підписки' : 'Billed according to standard tariffs or subscription'}
                            </p>
                          </div>
                        );
                      }
                      if (isUnlimitedAgent) {
                        return (
                          <div className="flex items-center gap-1.5 text-purple-700 font-bold bg-purple-50 px-2.5 py-1.5 rounded border border-purple-200">
                            <Sparkles className="w-3.5 h-3.5 text-purple-600 animate-pulse" />
                            <span>{isDe ? 'Unbegrenzter Zugang' : isFr ? 'Accès illimité' : isPl ? 'Dostęp nielimitowany' : isUk ? 'Безлімітний доступ' : 'Unlimited Access'}</span>
                          </div>
                        );
                      }
                      if (priceVal === 0) {
                        return (
                          <div className="flex items-center gap-1.5 text-emerald-600 font-semibold bg-emerald-50 px-2.5 py-1.5 rounded border border-emerald-100">
                            <span>✓</span>
                            <span>{isDe ? 'Kostenloser Zugang' : isFr ? 'Accès gratuit' : isPl ? 'Darmowy dostęp' : isUk ? 'Вільний доступ' : 'Free Access'}</span>
                          </div>
                        );
                      }
                      if (userSub) {
                        return (
                          <div className="bg-blue-50 border border-blue-100 rounded p-2.5 text-blue-700 space-y-1">
                            <div className="font-bold flex justify-between">
                              <span>{isDe ? 'AKTIVES ABONNEMENT' : isFr ? 'ABONNEMENT ACTIF' : isPl ? 'AKTYWNA SUBSKRYPCJA' : isUk ? 'АКТИВНА ПІДПИСКА' : 'ACTIVE SUBSCRIPTION'}</span>
                              <span className="text-blue-500">✓</span>
                            </div>
                            <div className="flex justify-between text-[9px] text-blue-600">
                              <span>{isDe ? 'Verbleibende Anfragen:' : isFr ? 'Demandes restantes :' : isPl ? 'Pozostało zapytań:' : isUk ? 'Залишилось запитів:' : 'Requests left:'}</span>
                              <span className="font-bold">
                                {Math.max(0, userSub.requests_limit - userSub.requests_used)} / {userSub.requests_limit}
                              </span>
                            </div>
                            <div className="flex justify-between text-[9px] text-blue-600">
                              <span>{isDe ? 'Gültig bis:' : isFr ? 'Expire le :' : isPl ? 'Ważna do:' : isUk ? 'Діє до:' : 'Expires:'}</span>
                              <span className="font-bold">{formatDate(userSub.expires_at)}</span>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded p-2.5 text-emerald-800 space-y-1">
                          <div className="flex items-center justify-between font-bold text-[10px]">
                            <span className="flex items-center gap-1.5 text-emerald-800 font-extrabold">
                              <Sparkles className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
                              <span>{isDe ? '🎁 1. Start KOSTENLOS' : isFr ? '🎁 1er essai GRATUIT' : isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Trial Launch FREE'}</span>
                            </span>
                            <span className="text-[8px] font-extrabold px-1.5 py-0.5 bg-emerald-600 text-white rounded uppercase tracking-wider">
                              100% FREE
                            </span>
                          </div>
                          <p className="text-[9px] text-emerald-700 font-semibold leading-tight">
                            {isDe ? 'Keine Gebühren • Keine Bankkarte erforderlich' : isFr ? 'Sans frais • Aucune carte bancaire requise' : isPl ? 'Brak opłat • Karta płatnicza nie jest wymagana' : isUk ? 'Плата не стягується • Банківська картка не потрібна' : 'No credit card or payment required'}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="pt-6">
                  <button
                    onClick={() => handleLaunchAgent(agent)}
                    className="w-full py-2.5 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-bold font-mono text-xs uppercase rounded-xl tracking-wider flex items-center justify-center gap-1.5 cursor-pointer shadow-xs transition-all hover:scale-[1.01]"
                  >
                    <span>{isDe ? 'MODUL STARTEN' : isFr ? 'LANCER LE MODULE' : isPl ? 'URUCHOM MODUŁ' : isUk ? 'ЗАПУСТИТИ АГЕНТА' : 'LAUNCH AGENT'}</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* 4. SYNERGY ADVERTISING SECTION */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 sm:p-6 font-mono space-y-4 shadow-xs">
        <h4 className="text-[10px] font-bold text-slate-800 uppercase tracking-widest flex items-center gap-2">
          <Network className="w-4 h-4 text-red-500" />
          <span>{isDe ? 'SYNERGIE DES MODUL-ÖKOSYSTEMS (AGENTS COOPERATION)' : isFr ? 'SYNERGIE DE L\'ÉCOSYSTÈME DES MODULES (AGENTS COOPERATION)' : isPl ? 'SYNERGIA EKOSYSTEMU MODUŁÓW (AGENTS COOPERATION)' : isUk ? 'СИНЕРГІЯ ЕКОСИСТЕМИ АГЕНТІВ (AGENTS COOPERATION)' : 'AGENTS COOPERATION SYNERGY'}</span>
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[10px] text-slate-600 leading-relaxed">
          <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-2">
            <p className="font-bold text-slate-800 flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 text-red-500" />
              <span>{isDe ? 'Schritt 1: Marktanalyse' : isFr ? 'Étape 1 : Analyse de marché' : isPl ? 'Krok 1: Analiza rynkowa' : isUk ? 'Крок 1: Маркетинговий аналіз' : 'Step 1: Market Intelligence'}</span>
            </p>
            <p>
              {isDe
                ? 'Nutzen Sie das Modul **Deep Research** zur Analyse globaler Märkte, Prüfung von Rohstofflieferanten und zum Vergleich kommerzieller Preisangebote.'
                : isFr
                ? 'Utilisez le module **Deep Research** pour analyser les marchés mondiaux, vérifier les fournisseurs et comparer les offres tarifaires.'
                : isPl
                ? 'Wykorzystaj moduł **Deep Research** do analizy rynków globalnych, weryfikacji dostawców surowców i porównywania ofert cenowych.'
                : isUk 
                ? 'Використовуйте агент **Deep Research** для аналізу світових ринків, перевірки постачальників матеріалів та порівняння комерційних пропозицій цін на сировину.'
                : 'Utilize the **Deep Research** agent to analyze global material supplies, verify raw material vendor bids, and perform deep competitive pricing research.'}
            </p>
          </div>

          <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-2">
            <p className="font-bold text-slate-800 flex items-center gap-1.5">
              <Calculator className="w-3.5 h-3.5 text-red-500" />
              <span>{isDe ? 'Schritt 2: Kostenschätzung' : isFr ? 'Étape 2 : Devis & Estimation' : isPl ? 'Krok 2: Kalkulacja kosztorysowa' : isUk ? 'Крок 2: Кошторисний розрахунок' : 'Step 2: Civil Cost Calculation'}</span>
            </p>
            <p>
              {isDe
                ? 'Übertragen Sie die ermittelten Marktpreisindizes an den Assistenten **Kalkulator / Kostenschätzer**, um die Berechnung anzupassen und die fertige Schätzung im Excel-Format zu generieren.'
                : isFr
                ? 'Transmettez les indices de prix moyens obtenus à l\'assistant **Devis / Métreur** pour ajuster les calculs et générer l\'estimation définitive au format Excel.'
                : isPl
                ? 'Przekaż uzyskane średnioroczne wskaźniki cenowe do asystenta **Kosztorysant** w celu precyzyjnego sporządzenia kosztorysu w formacie Excel.'
                : isUk 
                ? 'Передайте отримані середньоринкові ціни на матеріали асистенту **Кошторисник** для коригування розрахунку людино-годин та формування остаточного кошторису у форматі Excel.'
                : 'Feed research-derived pricing indices directly into **AI Estimator** parameters to adjust labor wages and produce client-ready estimates.'}
            </p>
          </div>
        </div>

        <p className="text-[9px] text-slate-400 text-center italic">
          {isDe
            ? 'Durch die Verbindung der analytischen Tiefe von Deep Research mit der ingenieurtechnischen Präzision des Kalkulators erhalten Sie einen vollständigen Projektzyklus ohne überflüssige Kosten.'
            : isFr
            ? 'En unissant la puissance cognitive de Deep Research à la précision technique du Métreur/Estimateur, vous bénéficiez d\'un cycle complet sans coûts superflus.'
            : isPl
            ? 'Łącząc analityczną moc Deep Research z inżynieryjną dokładnością Kosztorysanta, zyskujesz pełny cykl projektowania bez zbędnych kosztów.'
            : isUk 
            ? 'Поєднуючи аналітичну силу Deep Research та інженерну точність Кошторисника, ви отримуєте повний цикл проектування без додаткових витрат.'
            : 'Unifying the cognitive exploration of Deep Research with the mechanical accuracy of Estimator gives your team full-cycle lifecycle controls.'}
        </p>
      </div>

      {/* FEEDBACK MODAL */}
      {isFeedbackOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden font-mono text-xs text-slate-850">
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-600" />
                <span>{isDe ? 'Feedback / Kontakt' : isFr ? 'Avis / Contact' : isPl ? 'Opinia / Kontakt' : isUk ? 'Зворотній зв\'язок' : 'Feedback'}</span>
              </h3>
              <button
                onClick={() => setIsFeedbackOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSendFeedback} className="p-5 space-y-4">
              {feedbackSuccess ? (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-center space-y-2">
                  <p className="font-bold text-sm">
                    {isDe ? '✓ Vielen Dank für Ihre Nachricht!' : isFr ? '✓ Merci pour votre message !' : isPl ? '✓ Dziękujemy za Twoją wiadomość!' : isUk ? '✓ Дякуємо за ваше повідомлення!' : '✓ Thank you for your feedback!'}
                  </p>
                  <p className="text-[11px]">
                    {isDe ? 'Wir haben Ihr Feedback erhalten und werden es zeitnah prüfen.' : isFr ? 'Nous avons bien reçu votre avis et nous l\'examinerons sous peu.' : isPl ? 'Otrzymaliśmy Twoją opinię i przeanalizujemy ją wkrótce.' : isUk ? 'Ми отримали ваші побажання та розглянемо їх найближчим часом.' : 'We have received your message and will review it shortly.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsFeedbackOpen(false)}
                    className="mt-2 px-4 py-2 bg-emerald-600 text-white font-bold rounded-lg uppercase tracking-wider text-[10px]"
                  >
                    {isDe ? 'Schließen' : isFr ? 'Fermer' : isPl ? 'Zamknij' : isUk ? 'Закрити' : 'Close'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-1">
                    <label className="font-bold text-slate-700 uppercase text-[10px]">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={feedbackEmail}
                      onChange={(e) => setFeedbackEmail(e.target.value)}
                      placeholder="your.email@company.com"
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-700 uppercase text-[10px]">
                      {isDe ? 'Nachricht / Feedback' : isFr ? 'Message / Suggestions' : isPl ? 'Wiadomość / Sugestie' : isUk ? 'Повідомлення / Побажання' : 'Message / Feedback'}
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      placeholder={isDe ? 'Beschreiben Sie Ihre Frage oder Ihr Feedback zu den Modulen...' : isFr ? 'Décrivez votre question ou vos suggestions concernant les modules...' : isPl ? 'Opisz swoje pytanie lub sugestie dotyczące modułów...' : isUk ? 'Опишіть ваше запитання або побажання щодо роботи цифрових інструментів...' : 'Describe your question or feedback regarding AI agents...'}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs leading-relaxed"
                    />
                  </div>

                  {feedbackError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-[11px]">
                      {feedbackError}
                    </div>
                  )}

                  <div className="pt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setIsFeedbackOpen(false)}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg uppercase text-[10px]"
                    >
                      {isDe ? 'Abbrechen' : isFr ? 'Annuler' : isPl ? 'Anuluj' : isUk ? 'Скасувати' : 'Cancel'}
                    </button>
                    <button
                      type="submit"
                      disabled={isSendingFeedback}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg uppercase text-[10px] tracking-wider disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {isSendingFeedback ? (isDe ? 'Senden...' : isFr ? 'Envoi en cours...' : isPl ? 'Wysyłanie...' : isUk ? 'Надсилання...' : 'Sending...') : (isDe ? 'Senden' : isFr ? 'Envoyer' : isPl ? 'Wyślij' : isUk ? 'Надіслати' : 'Send')}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Deep Brain OS Modal Showcase & Checkout */}
      <DeepBrainOsShowcaseModal
        isOpen={showDeepBrainOsModal}
        onClose={() => setShowDeepBrainOsModal(false)}
        language={language}
        userEmail={currentUserEmail}
        initialTab={deepBrainOsInitialTab}
      />
    </div>
  );
}
